'use client'

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/getting-started/my-corner.mdx
 * (published at user-docs.totalaccess.co.za/getting-started/my-corner#profile)
 */

import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuth } from '@/hooks/useAuth'
import { useNavigation } from '@/hooks/useNavigation'
import Image from 'next/image'
import { clientService, type ClientInfo } from '@/lib/clientService'
import { OnboardWithClientModal } from '@/components/OnboardWithClientModal'
import ModalFooter from './ModalFooter'
import { debouncedEmailValidation, debouncedPhoneValidation } from '@/lib/emailPhoneValidation'
import type { ValidationResult } from '@/lib/emailPhoneValidation'
import { debouncedPasswordValidation } from '@/lib/passwordValidation'
import type { PasswordValidationResult } from '@/lib/passwordValidation'
import PhoneInput from '@/components/PhoneInput'
import { buildCentralPasskeyManagementUrl, buildCentralPasskeyRegistrationHandoffUrl, readCentralPasskeyRegistrationHandoffStateFromHash, type CentralPasskeyRegistrationHandoffState } from '@/lib/centralPasskeyFlow'
import { getEndpointUrl } from '@/lib/apiConfig'
import { apiFetch, apiGet, apiPatch } from '@/lib/apiFetch'
import { getRuntimePublicBaseUrl } from '@/lib/publicRuntimeConfig'
import { Capacitor } from '@capacitor/core'
import { messagingService, type PushDiagnosticsResult } from '@/lib/messagingService'
import { isStandalonePwa } from '@/lib/pwaAppIdentity'
import { QRCodeCanvas } from 'qrcode.react'
import { startRegistration } from '@simplewebauthn/browser'
import { Smartphone, Key, Shield, Plus, RefreshCw, Trash2, X, Download, Mail, MessageCircle, Hash } from 'lucide-react'
import Link from 'next/link'
import { notify, confirm } from '@/lib/notifications'
import { getCentralWebAuthnPortalOrigin, getWebAuthnScopeMessage, shouldUseCentralWebAuthnFlow } from '@/lib/webauthnRp'
import {
  DEFAULT_MESSAGE_SOUND_PREFERENCES,
  MESSAGE_SOUND_OPTIONS,
  getSoundOptionById,
  loadMessageSoundPreferences,
  playSoundFile,
  saveMessageSoundPreferences
} from '@/lib/messageSoundPreferences'
import type { MessageSoundOptionId, MessageSoundPreferences, MessageThreadSoundCategory } from '@/lib/messageSoundPreferences'
import {
  DEFAULT_CALL_SOUND_PREFERENCES,
  CALL_RINGTONE_OPTIONS,
  CALL_DIALTONE_OPTIONS,
  loadCallSoundPreferences,
  saveCallSoundPreferences,
  playCallSound,
  getRingtoneFilePath,
  getDialtoneFilePath
} from '@/lib/callSoundPreferences'
import type { CallSoundPreferences } from '@/lib/callSoundPreferences'
import {
  DEFAULT_PUSH_SOUND_PREFERENCES,
  PUSH_SOUND_OPTIONS,
  SOUND_PACK_OPTIONS,
  PUSH_NOTIFICATION_CATEGORIES,
  loadPushSoundPreferencesFromCache,
  fetchPushSoundPreferences,
  savePushSoundPreferences,
  getSoundFilePathWithPack,
  playPushSound
} from '@/lib/pushNotificationSoundPreferences'
import type {
  PushNotificationSoundPreferences,
  PushNotificationCategory,
  PushSoundOptionId,
  SoundPack
} from '@/lib/pushNotificationSoundPreferences'
import '@/app/professional-modern.css'
import type {
  User,
  Preferences,
  ClientMembership,
  PendingRequest,
  AvailableInvitation,
  JoinRequestItem,
  ManagedMfaMethod,
  OtpMethodStatus,
  TabType
} from './ProfileModal/types'
import { Header } from './ProfileModal/Header'
import { TabNav } from './ProfileModal/TabNav'
import { OverviewTab } from './ProfileModal/OverviewTab'
import { ContactsTab } from './ProfileModal/ContactsTab'
import { TermsTab } from './ProfileModal/TermsTab'

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback
}

// Editable Field Components
interface EditableFieldProps {
  label: string
  value: string
  fieldName: string
  type?: string
  placeholder?: string
  isEditing: boolean
  onEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
  error?: string
  requiresVerification?: boolean
}

function EditableField({
  label,
  value,
  fieldName,
  type = 'text',
  placeholder,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  saving,
  error,
  requiresVerification
}: EditableFieldProps) {
  const [editValue, setEditValue] = useState(value ?? '')

  const handleEdit = () => {
    setEditValue(value ?? '')
    onEdit()
  }

  const handleSave = () => {
    onSave(editValue)
  }

  const handleCancel = () => {
    setEditValue(value)
    onCancel()
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {requiresVerification && (
          <span className="text-orange-600 dark:text-orange-400 text-xs ml-2">Requires verification</span>
        )}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type={type}
          value={isEditing ? (editValue ?? '') : (value ?? '')}
          onChange={(e) => setEditValue(e.target.value)}
          disabled={!isEditing}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] dark:focus:ring-[var(--theme-accent)] disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        />
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:w-auto"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-3 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 text-sm sm:flex-none"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:flex-none"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}
    </div>
  )
}

interface EditableSelectFieldProps {
  label: string
  value: string
  fieldName: string
  options: { value: string; label: string }[]
  isEditing: boolean
  onEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
  error?: string
}

function EditableSelectField({
  label,
  value,
  fieldName,
  options,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  saving,
  error
}: EditableSelectFieldProps) {
  const [editValue, setEditValue] = useState(value ?? '')

  const handleEdit = () => {
    setEditValue(value ?? '')
    onEdit()
  }

  const handleSave = () => {
    onSave(editValue)
  }

  const handleCancel = () => {
    if (editValue !== (value ?? '')) setEditValue(value ?? '')
    onCancel()
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <select
          value={isEditing ? (editValue ?? '') : (value ?? '')}
          onChange={(e) => setEditValue(e.target.value)}
          disabled={!isEditing}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] dark:focus:ring-[var(--theme-accent)] disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:w-auto"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-3 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 text-sm sm:flex-none"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:flex-none"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}
    </div>
  )
}

interface EditableTextareaFieldProps {
  label: string
  value: string
  fieldName: string
  isEditing: boolean
  onEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
  error?: string
}

function EditableTextareaField({
  label,
  value,
  fieldName,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  saving,
  error
}: EditableTextareaFieldProps) {
  const [editValue, setEditValue] = useState(value ?? '')

  const handleEdit = () => {
    setEditValue(value ?? '')
    onEdit()
  }

  const handleSave = () => {
    onSave(editValue)
  }

  const handleCancel = () => {
    if (editValue !== (value ?? '')) setEditValue(value ?? '')
    onCancel()
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <textarea
          value={isEditing ? (editValue ?? '') : (value ?? '')}
          onChange={(e) => setEditValue(e.target.value)}
          disabled={!isEditing}
          rows={3}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] dark:focus:ring-[var(--theme-accent)] disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        />
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm self-start sm:w-auto"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2 self-start">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-3 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 text-sm sm:flex-none"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:flex-none"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}
    </div>
  )
}

// Enhanced Email Field Component with validation
interface EditableEmailFieldProps {
  label: string
  value: string
  fieldName: string
  isEditing: boolean
  onEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
  error?: string
  validation: ValidationResult
  requiresVerification?: boolean
  onValueChange?: (value: string) => void
}

function EditableEmailField({
  label,
  value,
  fieldName,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  saving,
  error,
  validation,
  requiresVerification,
  onValueChange
}: EditableEmailFieldProps) {
  const [editValue, setEditValue] = useState(value ?? '')

  const handleEdit = () => {
    setEditValue(value ?? '')
    onEdit()
  }

  const handleValueChange = (newValue: string) => {
    setEditValue(newValue)
    if (onValueChange) {
      onValueChange(newValue)
    }
  }

  const handleSave = () => {
    onSave(editValue)
  }

  const handleCancel = () => {
    if (editValue !== (value ?? '')) setEditValue(value ?? '')
    onCancel()
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
        {requiresVerification && (
          <span className="text-orange-600 dark:text-orange-400 text-xs ml-2">Requires verification</span>
        )}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="email"
          value={isEditing ? (editValue ?? '') : (value ?? '')}
          onChange={(e) => handleValueChange(e.target.value)}
          disabled={!isEditing}
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] dark:focus:ring-[var(--theme-accent)] disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        />
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:w-auto"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || validation.isChecking || validation.isValid === false}
              className="flex-1 px-3 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 text-sm sm:flex-none"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:flex-none"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {/* Validation messages */}
      {isEditing && validation.isChecking && (
        <div className="mt-1 text-sm text-[var(--theme-primary)]">
          Checking availability...
        </div>
      )}
      {isEditing && validation.error && !validation.isChecking && (
        <div className="mt-1 text-sm text-red-600">
          {validation.error}
        </div>
      )}
      {isEditing && !validation.error && !validation.isChecking && editValue && validation.isValid === true && (
        <div className="mt-1 text-sm text-green-600">
          ✓ Email is available
        </div>
      )}
      {error && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}
    </div>
  )
}

// Enhanced Phone Field Component with validation and country code dropdown
interface EditablePhoneFieldProps {
  label: string
  value: string
  fieldName: string
  isEditing: boolean
  onEdit: () => void
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
  error?: string
  validation: ValidationResult
  requiresVerification?: boolean
  onValueChange?: (value: string) => void
}

function EditablePhoneField({
  label,
  value,
  fieldName,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  saving,
  error,
  validation,
  requiresVerification,
  onValueChange
}: EditablePhoneFieldProps) {
  const [editValue, setEditValue] = useState(value ?? '')

  const handleEdit = () => {
    setEditValue(value ?? '')
    onEdit()
  }

  const handleValueChange = (newValue: string) => {
    setEditValue(newValue)
    if (onValueChange) {
      onValueChange(newValue)
    }
  }

  const handleSave = () => {
    onSave(editValue)
  }

  const handleCancel = () => {
    if (editValue !== (value ?? '')) setEditValue(value ?? '')
    onCancel()
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        {isEditing ? (
          <PhoneInput
            value={editValue}
            onChange={handleValueChange}
            className="flex-1 form-control"
            placeholder="Phone number"
          />
        ) : (
          <input
            type="tel"
            value={value ?? ''}
            disabled
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] dark:focus:ring-[var(--theme-accent)] disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
          />
        )}
        {!isEditing ? (
          <button
            type="button"
            onClick={handleEdit}
            className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:w-auto"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || validation.isChecking || validation.isValid === false}
              className="flex-1 px-3 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 text-sm sm:flex-none"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:flex-none"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {/* Validation messages */}
      {isEditing && validation.isChecking && (
        <div className="mt-1 text-sm text-[var(--theme-primary)]">
          Checking availability...
        </div>
      )}
      {isEditing && validation.error && !validation.isChecking && (
        <div className="mt-1 text-sm text-red-600">
          {validation.error}
        </div>
      )}
      {isEditing && !validation.error && !validation.isChecking && editValue && validation.isValid === true && (
        <div className="mt-1 text-sm text-green-600">
          ✓ Phone number is available
        </div>
      )}
      {error && (
        <p className="text-red-600 text-sm mt-1">{error}</p>
      )}
    </div>
  )
}

interface ProfileModalProps {
  isOpen?: boolean
  onClose?: () => void
  /** Open the modal on a specific tab (e.g. 'security' for PIN setup) */
  initialTab?: TabType
}

export default function ProfileModal({ isOpen = true, onClose, initialTab }: ProfileModalProps) {
  const { user: authUser, isAuthenticated, logout } = useAuth()
  const {
    user: navUser,
    userRole,
    navigationData,
    isAdmin,
    isManager,
    getAccessibleModules
  } = useNavigation()
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<TabType>(initialTab ?? 'personal')
  const [showWelcome, setShowWelcome] = useState(true)
  const router = useRouter()
  const searchParams = useSearchParams()
  const isNativePlatform = Capacitor.isNativePlatform()

  // OAuth link callback state
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null)
  const [linkErrorMsg, setLinkErrorMsg] = useState<string | null>(null)

  // Log component mount
  useEffect(() => {
    console.log('[PROFILE PAGE] Component mounted')
  }, [])

  // Monitor user data changes
  useEffect(() => {
    console.log('[PROFILE PAGE] User data updated')
  }, [authUser, navUser])

  // Handle welcome message transition
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(false)
    }, 10000) // 10 seconds

    return () => clearTimeout(timer)
  }, [])

  // Use navigation user data if available, fallback to auth user
  const user = navUser || authUser

  // Memoize accessible modules count using same method as dashboard
  const accessibleModulesCount = useMemo(() => {
    return navigationData ? getAccessibleModules().length : 0
  }, [navigationData, getAccessibleModules])

  // Personal Information State
  const [userDetails, setUserDetails] = useState({
    id: 0,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    avatar_url: '',
    title: '',
    dob: '',
    id_number: '',
    address: '',
    linkedin_url: '',
    google_id: null as string | null,
    microsoft_id: null as string | null,
    intuit_id: null as string | null,
    xero_id: null as string | null,
    membership_business_email: null as string | null,
    membership_business_phone: null as string | null
  })
  const [savingPersonal, setSavingPersonal] = useState(false)
  const [personalError, setPersonalError] = useState('')
  const [editingField, setEditingField] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})

  // Email and phone validation state
  const [emailValidation, setEmailValidation] = useState<ValidationResult>({ isValid: null, isChecking: false })
  const [phoneValidation, setPhoneValidation] = useState<ValidationResult>({ isValid: null, isChecking: false })

  // OTP verification state
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otpType, setOtpType] = useState<'email' | 'phone' | null>(null)
  const [otpModalMode, setOtpModalMode] = useState<'profile_update' | 'mfa_enable' | null>(null)
  const [pendingMfaOtpMethod, setPendingMfaOtpMethod] = useState<'email_otp' | 'whatsapp_otp' | null>(null)
  const [newContactValue, setNewContactValue] = useState('')
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', ''])
  const [resendCooldown, setResendCooldown] = useState(0)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [verifyingOtp, setVerifyingOtp] = useState(false)

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordErrors, setPasswordErrors] = useState<{ [key: string]: string }>({})
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidationResult>({ isValid: true })

  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // MFA State
  const [totpEnabled, setTotpEnabled] = useState(false)
  const [showTotpSetup, setShowTotpSetup] = useState(false)
  const [showTotpDisable, setShowTotpDisable] = useState(false)
  const [totpSetup, setTotpSetup] = useState<{ secret: string; qrCode: string; otpauthUrl: string; manualEntryKey: string } | null>(null)
  const [totpVerificationCode, setTotpVerificationCode] = useState('')
  const [totpDisablePassword, setTotpDisablePassword] = useState('')
  const [totpDisableCode, setTotpDisableCode] = useState('')
  const [totpLoading, setTotpLoading] = useState(false)
  const [totpError, setTotpError] = useState('')
  const [totpSuccess, setTotpSuccess] = useState('')
  const [emailOtpEnabled, setEmailOtpEnabled] = useState(false)
  const [whatsappOtpEnabled, setWhatsappOtpEnabled] = useState(false)
  const [inAppApprovalEnabled, setInAppApprovalEnabled] = useState(false)
  const [webauthnMfaEnabled, setWebauthnMfaEnabled] = useState(false)
  const [preferredMfaMethodLoading, setPreferredMfaMethodLoading] = useState<ManagedMfaMethod | null>(null)
  const [emailOtpStatus, setEmailOtpStatus] = useState<OtpMethodStatus | null>(null)
  const [whatsappOtpStatus, setWhatsappOtpStatus] = useState<OtpMethodStatus | null>(null)
  const [inAppApprovalStatus, setInAppApprovalStatus] = useState<OtpMethodStatus | null>(null)
  const [webauthnMfaStatus, setWebauthnMfaStatus] = useState<OtpMethodStatus | null>(null)
  const [otpMethodLoading, setOtpMethodLoading] = useState<'email_otp' | 'whatsapp_otp' | 'inapp_approval' | 'webauthn' | null>(null)
  const [showInAppApprovalModal, setShowInAppApprovalModal] = useState(false)
  const [pendingInAppApprovalSessionId, setPendingInAppApprovalSessionId] = useState<string | null>(null)
  const [pendingInAppApprovalExpiresAt, setPendingInAppApprovalExpiresAt] = useState<string | null>(null)
  const [pendingInAppApprovalState, setPendingInAppApprovalState] = useState<'pending' | 'approved' | 'denied' | 'expired' | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [showBackupCodes, setShowBackupCodes] = useState(false)
  const [webauthnCredentials, setWebauthnCredentials] = useState<Array<{
    id: string;
    device_name: string;
    device_type: string | null;
    created_at: string;
    last_used: string | null;
  }>>([])
  const [webauthnLoading, setWebauthnLoading] = useState(false)
  const [webauthnError, setWebauthnError] = useState('')
  const [webauthnSuccess, setWebauthnSuccess] = useState('')
  const [deviceName, setDeviceName] = useState('')

  // Lock Screen PIN State
  const [hasPin, setHasPin] = useState(false)
  const [pinLoading, setPinLoading] = useState(false)
  const [pinError, setPinError] = useState('')
  const [pinSuccess, setPinSuccess] = useState('')
  const [showPinSetup, setShowPinSetup] = useState(false)
  const [pinData, setPinData] = useState({ pin: '', confirmPin: '', password: '' })

  // Account Linking State
  const [linkingAccount, setLinkingAccount] = useState(false)
  const [linkingError, setLinkingError] = useState('')

  // OTP Verification State
  const [otpVerification, setOtpVerification] = useState({
    required: false,
    contactMethod: '',
    purpose: '',
    otpCode: '',
    sendingOtp: false,
    verifyingOtp: false
  })

  // Push Notification State (for mobile app)
  const [pushNotificationStatus, setPushNotificationStatus] = useState<'loading' | 'granted' | 'denied' | 'prompt' | 'unavailable'>('loading')
  const [enablingPush, setEnablingPush] = useState(false)
  const [pushError, setPushError] = useState('')
  const [pushSuccess, setPushSuccess] = useState('')
  const [runningPushDiagnostics, setRunningPushDiagnostics] = useState(false)
  const [pushDiagnostics, setPushDiagnostics] = useState<PushDiagnosticsResult | null>(null)

  // Biometric Unlock State (for mobile app)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  const [biometricType, setBiometricType] = useState<'fingerprint' | 'face' | 'iris' | 'multiple' | 'none'>('none')
  const [biometricEnabled, setBiometricEnabled] = useState(false)
  const [biometricLoading, setBiometricLoading] = useState(false)

  // Preferences State
  const [preferences, setPreferences] = useState<Preferences>({
    emailNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    securityAlerts: true,
    ticketUpdates: true,
    newsletter: false,
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    theme: 'light',
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '07:00'
  })
  const [savingPreferences, setSavingPreferences] = useState(false)
  const [preferencesErrors, setPreferencesErrors] = useState<{ [key: string]: string }>({})
  const [preferencesSuccess, setPreferencesSuccess] = useState('')

  // Message notification sounds (local per-device prefs for now)
  const [messageSoundPrefs, setMessageSoundPrefs] = useState<MessageSoundPreferences>(() => loadMessageSoundPreferences())

  const updateMessageSoundPref = (category: MessageThreadSoundCategory, optionId: MessageSoundOptionId) => {
    const next: MessageSoundPreferences = { ...messageSoundPrefs, [category]: optionId }
    setMessageSoundPrefs(next)
    saveMessageSoundPreferences(next)
  }

  const resetMessageSoundPrefs = () => {
    setMessageSoundPrefs({ ...DEFAULT_MESSAGE_SOUND_PREFERENCES })
    saveMessageSoundPreferences({ ...DEFAULT_MESSAGE_SOUND_PREFERENCES })
  }

  const previewMessageSound = (optionId: MessageSoundOptionId) => {
    const option = getSoundOptionById(optionId)
    playSoundFile(option.filePath, 0.6)
  }

  // Call sound preferences (ringtone, dial tone)
  const [callSoundPrefs, setCallSoundPrefs] = useState<CallSoundPreferences>(() => loadCallSoundPreferences())
  const [previewingAudio, setPreviewingAudio] = useState<HTMLAudioElement | null>(null)

  // Push notification sound preferences (email, calendar, etc.)
  const [pushSoundPrefs, setPushSoundPrefs] = useState<PushNotificationSoundPreferences>(() => loadPushSoundPreferencesFromCache())
  const [pushSoundPrefsLoading, setPushSoundPrefsLoading] = useState(false)
  const [pushSoundPrefsSaving, setPushSoundPrefsSaving] = useState(false)
  const supportsPushSoundPreferences = Capacitor.isNativePlatform() || (typeof window !== 'undefined' && isStandalonePwa())
  const isMobileMfaContext = Capacitor.isNativePlatform() || (typeof window !== 'undefined' && isStandalonePwa())
  const passkeysSupportedOnThisDevice = !Capacitor.isNativePlatform() && !(typeof window !== 'undefined' && isStandalonePwa())
  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : ''
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  const passkeyScopeMessage = useMemo(
    () => (currentHostname ? getWebAuthnScopeMessage(currentHostname) : null),
    [currentHostname]
  )
  const centralPasskeyOrigin = useMemo(
    () => (currentHostname ? getCentralWebAuthnPortalOrigin(currentHostname) : ''),
    [currentHostname]
  )
  const requiresCentralPasskeyManagement = useMemo(
    () => Boolean(passkeysSupportedOnThisDevice && currentHostname && shouldUseCentralWebAuthnFlow(currentHostname)),
    [currentHostname, passkeysSupportedOnThisDevice]
  )
  const centralPasskeyHostLabel = useMemo(() => {
    if (!centralPasskeyOrigin) {
      return 'central sign-in'
    }

    try {
      return new URL(centralPasskeyOrigin).host
    } catch {
      return centralPasskeyOrigin
    }
  }, [centralPasskeyOrigin])
  const centralPasskeyManagementUrl = useMemo(() => {
    if (!centralPasskeyOrigin || typeof window === 'undefined') {
      return null
    }

    return buildCentralPasskeyManagementUrl({
      centralOrigin: centralPasskeyOrigin,
      returnUrl: window.location.href,
    })
  }, [centralPasskeyOrigin])
  const centralPasskeyReturnUrl = useMemo(() => {
    const returnUrl = searchParams.get('passkey_return')

    if (!returnUrl) {
      return null
    }

    try {
      return new URL(returnUrl)
    } catch {
      return null
    }
  }, [searchParams])

  // Fetch push sound preferences from server on mount
  useEffect(() => {
    if (supportsPushSoundPreferences && isOpen) {
      setPushSoundPrefsLoading(true)
      fetchPushSoundPreferences()
        .then(prefs => setPushSoundPrefs(prefs))
        .finally(() => setPushSoundPrefsLoading(false))
    }
  }, [supportsPushSoundPreferences, isOpen])

  // Fetch user preferences (quiet hours, notification toggles) from server on mount
  useEffect(() => {
    if (!isAuthenticated || !isOpen) return
    apiGet('/api/v1/preferences').then(async res => {
      const data = await res.json()
      if (data?.success && data?.preferences) {
        const p = data.preferences
        const qh = p.quietHours || {}
        setPreferences(prev => ({
          ...prev,
          emailNotifications: p.emailNotifications ?? prev.emailNotifications,
          smsNotifications: p.smsNotifications ?? prev.smsNotifications,
          marketingEmails: p.marketingEmails ?? prev.marketingEmails,
          securityAlerts: p.securityAlerts ?? prev.securityAlerts,
          quietHoursEnabled: qh.enabled ?? false,
          quietHoursStart: qh.start || '22:00',
          quietHoursEnd: qh.end || '07:00'
        }))
      }
    }).catch(err => console.error('[Profile] Failed to load preferences:', err))
  }, [isAuthenticated, isOpen])

  // Check biometric availability on mount (mobile only)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const checkBiometric = async () => {
      try {
        const { biometricAuth } = await import('@/plugins/BiometricAuth')
        const availability = await biometricAuth.isAvailable()
        setBiometricAvailable(availability.isAvailable)
        setBiometricType(availability.biometryType)
        setBiometricEnabled(biometricAuth.isEnabled())
        console.log('[Profile] Biometric availability:', availability)
      } catch (error) {
        console.error('[Profile] Failed to check biometric availability:', error)
      }
    }
    checkBiometric()
  }, [])

  const updatePushSoundPref = async <K extends keyof PushNotificationSoundPreferences>(
    key: K,
    value: PushNotificationSoundPreferences[K]
  ) => {
    const next: PushNotificationSoundPreferences = { ...pushSoundPrefs, [key]: value }
    setPushSoundPrefs(next)
    setPushSoundPrefsSaving(true)
    await savePushSoundPreferences({ [key]: value })
    setPushSoundPrefsSaving(false)
  }

  const resetPushSoundPrefs = async () => {
    setPushSoundPrefs({ ...DEFAULT_PUSH_SOUND_PREFERENCES })
    setPushSoundPrefsSaving(true)
    await savePushSoundPreferences({ ...DEFAULT_PUSH_SOUND_PREFERENCES })
    setPushSoundPrefsSaving(false)
  }

  const previewPushSound = (category: PushNotificationCategory) => {
    // Stop any currently previewing audio
    if (previewingAudio) {
      previewingAudio.pause()
      previewingAudio.currentTime = 0
    }
    const soundId = pushSoundPrefs[category] as PushSoundOptionId
    const filePath = getSoundFilePathWithPack(pushSoundPrefs.soundPack, soundId)
    if (filePath) {
      const audio = playPushSound(filePath, pushSoundPrefs.volume)
      if (audio) {
        setPreviewingAudio(audio)
        audio.onended = () => setPreviewingAudio(null)
      }
    }
  }

  const updateCallSoundPref = (key: keyof CallSoundPreferences, value: string | number) => {
    const next: CallSoundPreferences = { ...callSoundPrefs, [key]: value }
    setCallSoundPrefs(next)
    saveCallSoundPreferences(next)
  }

  const resetCallSoundPrefs = () => {
    setCallSoundPrefs({ ...DEFAULT_CALL_SOUND_PREFERENCES })
    saveCallSoundPreferences({ ...DEFAULT_CALL_SOUND_PREFERENCES })
  }

  const previewCallSound = (type: 'ringtone' | 'dialtone') => {
    // Stop any currently previewing audio
    if (previewingAudio) {
      previewingAudio.pause()
      previewingAudio.currentTime = 0
    }

    const filePath = type === 'ringtone'
      ? getRingtoneFilePath(callSoundPrefs)
      : getDialtoneFilePath(callSoundPrefs)

    if (filePath) {
      const volume = type === 'ringtone' ? callSoundPrefs.ringtoneVolume : callSoundPrefs.dialToneVolume
      const audio = playCallSound(filePath, volume)
      setPreviewingAudio(audio)

      // Auto-stop after 3 seconds for preview
      if (audio) {
        setTimeout(() => {
          audio.pause()
          audio.currentTime = 0
          setPreviewingAudio(null)
        }, 3000)
      }
    }
  }

  // Client Information State
  const [clientMemberships, setClientMemberships] = useState<ClientMembership[]>([])
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([])
  const [availableInvitations, setAvailableInvitations] = useState<AvailableInvitation[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [showOnboardingModal, setShowOnboardingModal] = useState(false)

  // Avatar Upload State
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState('')
  const [avatarSuccess, setAvatarSuccess] = useState('')
  const [showAvatarOptions, setShowAvatarOptions] = useState(false)

  const fetchUserProfile = useCallback(async () => {
    if (!user) return

    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      if (!token) return

      const response = await fetch('/api/v1/user/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setUserDetails(prev => ({
            ...prev,
            ...data.data,
            title: data.data.title ?? prev.title ?? '',
            dob: data.data.dob ?? prev.dob ?? '',
            id_number: data.data.id_number ?? prev.id_number ?? '',
            address: data.data.address ?? prev.address ?? '',
            phone: data.data.phone ?? prev.phone ?? '',
            first_name: data.data.first_name ?? prev.first_name ?? '',
            last_name: data.data.last_name ?? prev.last_name ?? '',
            avatar_url: data.data.avatar_url ?? prev.avatar_url ?? '',
            linkedin_url: data.data.linkedin_url ?? prev.linkedin_url ?? '',
            google_id: data.data.google_id ?? prev.google_id ?? null,
            microsoft_id: data.data.microsoft_id ?? prev.microsoft_id ?? null,
            intuit_id: data.data.intuit_id ?? prev.intuit_id ?? null,
            xero_id: data.data.xero_id ?? prev.xero_id ?? null,
            membership_business_email: data.data.membership_business_email ?? prev.membership_business_email ?? null,
            membership_business_phone: data.data.membership_business_phone ?? prev.membership_business_phone ?? null
          }))
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }, [user])

  async function uploadAvatar(file: File) {
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('File size must be less than 5MB')
      return
    }

    if (!file.type.startsWith('image/')) {
      setAvatarError('Please select a valid image file')
      return
    }

    setUploadingAvatar(true)
    setAvatarError('')
    setAvatarSuccess('')
    setShowAvatarOptions(false)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('https://staticfileserver.totalaccess.co.za/upload', {
        method: 'POST',
        headers: {
          'x-api-key': 'a11abd7b-0d47-4fe2-a66f-f7c405cf5f80'
        },
        body: formData
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to upload avatar')
      }

      const avatarUrl = data.url || data.fileUrl || data.data?.url

      setUserDetails(prev => ({
        ...prev,
        avatar_url: avatarUrl || prev.avatar_url
      }))

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      if (!token) {
        throw new Error('Authentication required')
      }

      const updateResponse = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ avatar_url: avatarUrl })
      })

      const updateData = await updateResponse.json()

      if (!updateResponse.ok || !updateData.success) {
        throw new Error(updateData.error || 'Failed to update avatar')
      }

      setAvatarSuccess('Profile photo updated successfully')
      await fetchUserProfile()
    } catch (error: unknown) {
      console.error('Avatar upload error:', error)
      setAvatarError(getErrorMessage(error, 'Failed to upload avatar'))
    } finally {
      setUploadingAvatar(false)
    }
  }

  // Handle OAuth link callback from URL params
  useEffect(() => {
    const tab = searchParams.get('tab')
    const linkSuccessParam = searchParams.get('link_success')
    const linkErrorParam = searchParams.get('link_error')
    const provider = searchParams.get('provider')

    // Switch to security tab if specified
    if (tab === 'security') {
      setActiveTab('security')
    }

    // Handle link success
    if (linkSuccessParam === 'true' && provider) {
      setLinkSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} account successfully linked!`)
      // Refresh user profile to get updated OAuth IDs
      fetchUserProfile()
      // Clear URL params
      router.replace('/client-zone/profile?tab=security', { scroll: false })
      // Auto-clear success message after 5 seconds
      setTimeout(() => setLinkSuccess(null), 5000)
    }

    // Handle link error
    if (linkErrorParam && provider) {
      const errorMessages: Record<string, string> = {
        'already_linked': `This ${provider} account is already linked to another user.`,
        'failed': `Failed to link ${provider} account. Please try again.`
      }
      setLinkErrorMsg(errorMessages[linkErrorParam] || `Error linking ${provider} account.`)
      // Clear URL params
      router.replace('/client-zone/profile?tab=security', { scroll: false })
      // Auto-clear error after 8 seconds
      setTimeout(() => setLinkErrorMsg(null), 8000)
    }
  }, [searchParams, router, fetchUserProfile])

  useEffect(() => {
    if (!isOpen) return

    console.log('[PROFILE PAGE] Auth check - isAuthenticated:', isAuthenticated, 'hasUser:', !!user)

    if (!isAuthenticated) {
      console.log('[PROFILE PAGE] User not authenticated, redirecting to /auth/login')
      router.push('/auth/login')
      return
    }

    console.log('[PROFILE PAGE] User authenticated, proceeding with initialization')

    // Initialize data
    if (user) {
      console.log('[PROFILE PAGE] Initializing user data:', user?.email || 'unknown')
      // Handle both User and UserInfo types
      const userName = 'name' in user ? user.name : `${user.first_name || ''} ${user.last_name || ''}`.trim()
      const [firstName, ...lastNameParts] = userName.split(' ')
      const lastName = lastNameParts.join(' ')

      setUserDetails({
        id: user.id ?? 0,
        email: user.email ?? '',
        first_name: 'first_name' in user ? (user.first_name ?? '') : (firstName ?? ''),
        last_name: 'last_name' in user ? (user.last_name ?? '') : (lastName ?? ''),
        phone: 'phone' in user ? (user.phone ?? '') : '',
        avatar_url: user.avatar_url ?? '',
        title: 'title' in user && typeof user.title === 'string' ? user.title : '',
        dob: 'dob' in user && typeof user.dob === 'string' ? user.dob : '',
        id_number: 'id_number' in user && typeof user.id_number === 'string' ? user.id_number : '',
        address: 'address' in user && typeof user.address === 'string' ? user.address : '',
        linkedin_url: 'linkedin_url' in user && typeof user.linkedin_url === 'string' ? user.linkedin_url : '',
        google_id: 'google_id' in user ? (user.google_id as string | null) : null,
        microsoft_id: 'microsoft_id' in user ? (user.microsoft_id as string | null) : null,
        intuit_id: 'intuit_id' in user ? (user.intuit_id as string | null) : null,
        xero_id: 'xero_id' in user ? (user.xero_id as string | null) : null,
        membership_business_email: null,
        membership_business_phone: null
      })

      // Fetch complete user profile from API
      fetchUserProfile()
    }

    setLoading(false)
  }, [isAuthenticated, router, user, fetchUserProfile, isOpen])

  // Fetch client information
  const fetchClientData = useCallback(async () => {
    if (!user || !isAuthenticated) return

    try {
      setLoadingClients(true)

      // Use clientService to get user clients (same as ClientSelector)
      const clientData = await clientService.getUserClients()
      console.log('[PROFILE] Client data received:', clientData)

      if (!clientData) {
        console.warn('[PROFILE] No client data returned from clientService.getUserClients()')
        return
      }

      if (clientData.success) {
        console.log('[PROFILE] Available clients:', clientData.available_clients)
        // Separate active and pending memberships
        // Filter out client_id 99999 (placeholder for users without a client)
        const activeMemberships = clientData.available_clients.filter(client =>
          client.membership_status === 'active' && client.id !== 99999
        )
        const pendingMemberships = clientData.available_clients.filter(client =>
          client.membership_status === 'pending' && client.id !== 99999
        )

        // Transform to our interface format
        const formattedActiveMemberships: ClientMembership[] = activeMemberships.map(client => ({
          id: client.id,
          name: client.name,
          trading_name: client.trading_name || undefined,
          role: client.role,
          is_default: client.is_default,
          is_current_client: client.is_current_client || false,
          membership_status: (client.membership_status as 'active' | 'pending' | 'inactive') || 'active',
          is_active: client.is_active || false,
          joined_at: undefined // This would need to be fetched separately if needed
        }))

        const formattedPendingRequests: PendingRequest[] = pendingMemberships.map(client => ({
          id: client.id,
          name: client.name,
          trading_name: client.trading_name || undefined,
          role: client.role,
          membership_status: 'pending',
          requested_at: new Date().toISOString(), // This would need to be fetched separately if needed
          request_details: undefined
        }))

        setClientMemberships(formattedActiveMemberships)
        setPendingRequests(formattedPendingRequests)
      }

      // Fetch available invitations (where email or phone matches user's details)
      // Note: This endpoint may not exist yet, so we'll skip it for now to prevent loading issues
      /*
      const phoneParam = user && 'phone' in user && user.phone ? `&phone=${user.phone}` : ''
      const token = (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return localStorage.getItem('token');
        }
      })();

      const invitationsResponse = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:3001'}/api/v1/user_invitations/available?email=${user.email}${phoneParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (invitationsResponse.ok) {
        const invitationsData = await invitationsResponse.json()
        if (invitationsData.success) {
          setAvailableInvitations(invitationsData.invitations)
        }
      }
      */

    } catch (error) {
      console.error('Error fetching client data:', error)
    } finally {
      setLoadingClients(false)
    }
  }, [user, isAuthenticated])

  // Fetch user's join requests from our new API
  const fetchJoinRequests = useCallback(async () => {
    if (!user || !isAuthenticated) return

    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();
      if (!token) {
        console.log('[PROFILE] No token found for join requests')
        return
      }

      const response = await fetch('/api/v1/user/join-requests', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          const joinRequests: JoinRequestItem[] = Array.isArray(data.join_requests)
            ? data.join_requests as JoinRequestItem[]
            : []

          // Convert the join requests to the expected format
          // Filter out client_id 99999 (placeholder for users without a client)
          const formattedJoinRequests: PendingRequest[] = joinRequests
            .filter((request) => request.client_id !== 99999)
            .map((request) => ({
              id: request.id,
              name: request.client_name,
              trading_name: undefined, // Not available in join requests
              role: 'member', // Default role for join requests
              membership_status: 'pending' as const,
              requested_at: request.created_at,
              request_details: request.details
            }))

          // Add these to the existing pending requests (keep the ones from clientService)
          setPendingRequests(prev => {
            // Combine and deduplicate by client name
            const combined = [...prev, ...formattedJoinRequests]
            const unique = combined.filter((item, index, self) =>
              index === self.findIndex(t => t.name === item.name)
            )
            return unique
          })
          console.log('[PROFILE] Join requests loaded:', formattedJoinRequests)
        }
      } else {
        console.error('[PROFILE] Failed to fetch join requests:', response.status)
      }

    } catch (error) {
      console.error('[PROFILE] Error fetching join requests:', error)
    }
  }, [user, isAuthenticated])

  // Key these effects on stable primitives (user id / client id) rather than the
  // user/navigationData objects — those get new references on every navigation
  // background refresh, which re-fired these effects and refetched clients and
  // join requests in a loop. Callbacks are invoked via refs so the latest
  // closure is always used without making the effect depend on their identity.
  const userId = user?.id ?? null
  const navigationClientId = navigationData?.client_info?.id ?? null
  const fetchClientDataRef = useRef(fetchClientData)
  fetchClientDataRef.current = fetchClientData
  const fetchJoinRequestsRef = useRef(fetchJoinRequests)
  fetchJoinRequestsRef.current = fetchJoinRequests

  // Load client data when component mounts — only when the modal is open;
  // it stays mounted (closed) inside the global Header on every page.
  useEffect(() => {
    if (isAuthenticated && isOpen && userId) {
      fetchClientDataRef.current()
      fetchJoinRequestsRef.current()
    }
  }, [isAuthenticated, isOpen, userId])

  // Refresh client data when the active client changes (client switch)
  useEffect(() => {
    if (isAuthenticated && isOpen && userId && navigationClientId) {
      console.log('[PROFILE PAGE] Active client changed, refreshing client data')
      fetchClientDataRef.current()
      fetchJoinRequestsRef.current()
    }
  }, [navigationClientId, isAuthenticated, isOpen, userId])

  // Check push notification status on mount (for mobile app)
  useEffect(() => {
    const checkPushStatus = async () => {
      if (!Capacitor.isNativePlatform()) {
        if (typeof window === 'undefined' || !isStandalonePwa()) {
          setPushNotificationStatus('unavailable')
          return
        }

        try {
          const supported = await messagingService.isSupported()
          if (!supported) {
            setPushNotificationStatus('unavailable')
            return
          }

          if (Notification.permission === 'granted') {
            setPushNotificationStatus('granted')
          } else if (Notification.permission === 'denied') {
            setPushNotificationStatus('denied')
          } else {
            setPushNotificationStatus('prompt')
          }
        } catch (error) {
          console.error('[PROFILE] Error checking PWA push notification status:', error)
          setPushNotificationStatus('unavailable')
        }

        return
      }

      try {
        const { PushNotifications } = await import('@capacitor/push-notifications')
        const status = await PushNotifications.checkPermissions()
        setPushNotificationStatus(status.receive as 'granted' | 'denied' | 'prompt')
      } catch (error) {
        console.error('[PROFILE] Error checking push notification status:', error)
        setPushNotificationStatus('unavailable')
      }
    }

    checkPushStatus()
  }, [])

  // Handle enabling push notifications
  const handleEnablePushNotifications = async () => {
    setEnablingPush(true)
    setPushError('')
    setPushSuccess('')

    try {
      if (Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import('@capacitor/push-notifications')

        // Request permission
        const permResult = await PushNotifications.requestPermissions()

        if (permResult.receive === 'granted') {
          // Now register for push notifications
          const deviceServiceModule = await import('@/lib/capacitorDeviceService')
          const deviceService = deviceServiceModule.capacitorDeviceService

          await deviceService.initialize()

          // Set up listeners and register
          const token = await deviceService.requestPushNotificationPermission()

          if (token) {
            console.log('[PROFILE] Push notification token received')
            // Get user ID for server registration
            const userDataJson = localStorage.getItem('user_data')
            if (userDataJson) {
              const userData = JSON.parse(userDataJson)
              await deviceService.registerFCMTokenWithServer(userData.id)
            }
          }

          setPushNotificationStatus('granted')
          setPushSuccess('Push notifications are enabled for this device.')
          notify.success('Push notifications enabled')
        } else {
          setPushNotificationStatus(permResult.receive as 'denied' | 'prompt')
          throw new Error('Permission was not granted. You can enable notifications in your device settings.')
        }

        return
      }

      if (typeof window === 'undefined' || !isStandalonePwa()) {
        setPushNotificationStatus('unavailable')
        throw new Error('Install the TotalAccess PWA on this device to enable push notifications here.')
      }

      const supported = await messagingService.isSupported()
      if (!supported) {
        setPushNotificationStatus('unavailable')
        throw new Error('Push notifications are not supported in this browser or PWA runtime.')
      }

      const initialized = await messagingService.initializeFCM()
      if (!initialized && (typeof Notification === 'undefined' || Notification.permission !== 'granted')) {
        throw new Error('Push notification setup could not be initialized on this device.')
      }

      const token = await messagingService.requestNotificationPermission()
      if (!token) {
        const permission = typeof Notification !== 'undefined' ? Notification.permission : 'default'
        setPushNotificationStatus(permission === 'granted' ? 'granted' : permission === 'denied' ? 'denied' : 'prompt')

        if (permission === 'denied') {
          throw new Error('Notification permission is blocked. Please allow notifications for this installed app in your browser or device settings.')
        }

        throw new Error('The browser did not return a push token. Please keep the installed PWA open and try again.')
      }

      const registered = await messagingService.ensureTokenRegistered()
      if (!registered) {
        setPushNotificationStatus('granted')
        throw new Error('Permission was granted, but the push token could not be registered with the server yet. Please try again in a few seconds.')
      }

      setPushNotificationStatus('granted')
      setPushSuccess('Push notifications are enabled for this installed PWA and linked to your current session.')
      notify.success('Push notifications enabled')
    } catch (error: unknown) {
      console.error('[PROFILE] Error enabling push notifications:', error)
      const message = getErrorMessage(error, 'Failed to enable push notifications')
      setPushError(message)
      notify.error(message)
    } finally {
      setEnablingPush(false)
    }
  }

  const handleRunPushDiagnostics = async () => {
    setRunningPushDiagnostics(true)
    setPushError('')
    setPushSuccess('')

    try {
      if (Capacitor.isNativePlatform()) {
        setPushDiagnostics(null)
        setPushSuccess('Push diagnostics for native mobile are not wired into this panel yet. Use the native push flow above.')
        return
      }

      if (typeof window === 'undefined' || !isStandalonePwa()) {
        setPushDiagnostics(null)
        setPushError('Open the installed PWA on the device to run browser push diagnostics.')
        return
      }

      const diagnostics = await messagingService.runDiagnostics()
      setPushDiagnostics(diagnostics)

      if (diagnostics.error) {
        setPushError(diagnostics.error)
      } else if (diagnostics.registrationSucceeded) {
        setPushSuccess('Push diagnostics completed and confirmed token registration success.')
      } else if (!diagnostics.tokenFetchSucceeded) {
        setPushError('Diagnostics completed: the browser still did not produce a push token.')
      } else if (!diagnostics.registrationSucceeded) {
        setPushError('Diagnostics completed: a token was created, but server registration still failed.')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to run push diagnostics'
      setPushError(message)
      setPushDiagnostics(null)
    } finally {
      setRunningPushDiagnostics(false)
    }
  }

  const userDisplayName = () => {
    if (!user) return 'User'

    // Handle UserInfo type (has 'name' property)
    if ('name' in user && user.name) {
      return user.name
    }

    // Handle User type (has 'first_name' and 'last_name' properties)
    if ('first_name' in user || 'last_name' in user) {
      const firstName = 'first_name' in user ? user.first_name || '' : ''
      const lastName = 'last_name' in user ? user.last_name || '' : ''
      const fullName = `${firstName} ${lastName}`.trim()
      if (fullName) return fullName
    }

    // Fallback to email
    if (user.email) {
      return user.email.split('@')[0]
    }

    return 'User'
  }

  const userEmail = user?.email || ''
  const profileImageUrl = useMemo(() => user?.avatar_url, [user?.avatar_url])
  // Phone number normalization function
  const normalizePhoneNumber = (phone: string): string => {
    if (!phone) return phone

    // Remove all non-digit characters except +
    const cleaned = phone.replace(/[^\d+]/g, '')

    // If it starts with 0 and doesn't have country code, add +27
    if (cleaned.startsWith('0') && !cleaned.startsWith('+')) {
      return '+27' + cleaned.slice(1)
    }

    // If it doesn't start with + and doesn't start with 0, add +27
    if (!cleaned.startsWith('+') && !cleaned.startsWith('0')) {
      return '+27' + cleaned
    }

    // If it already has +, return as is
    if (cleaned.startsWith('+')) {
      return cleaned
    }

    return cleaned
  }

  // Email validation effect
  useEffect(() => {
    if (editingField === 'email' && userDetails.email && userDetails.email !== user?.email) {
      debouncedEmailValidation(userDetails.email, setEmailValidation)
    } else {
      setEmailValidation({ isValid: null, isChecking: false })
    }

    // Cleanup timeout on unmount
    return () => {
      if (globalThis.__emailValidationTimeout) {
        clearTimeout(globalThis.__emailValidationTimeout)
      }
    }
  }, [userDetails.email, editingField, user?.email])

  // Phone validation effect  
  useEffect(() => {
    const userPhone = user && 'phone' in user ? user.phone : ''
    if (editingField === 'phone' && userDetails.phone && userDetails.phone !== userPhone) {
      const normalizedPhone = normalizePhoneNumber(userDetails.phone)
      debouncedPhoneValidation(normalizedPhone, setPhoneValidation)
    } else {
      setPhoneValidation({ isValid: null, isChecking: false })
    }

    // Cleanup timeout on unmount
    return () => {
      if (globalThis.__phoneValidationTimeout) {
        clearTimeout(globalThis.__phoneValidationTimeout)
      }
    }
  }, [userDetails.phone, editingField, user])

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Password validation effect
  useEffect(() => {
    if (passwordData.newPassword) {
      debouncedPasswordValidation(passwordData.newPassword, setPasswordValidation)
    } else {
      setPasswordValidation({ isValid: true })
    }

    // Cleanup timeout on unmount
    return () => {
      if (globalThis.__passwordValidationTimeout) {
        clearTimeout(globalThis.__passwordValidationTimeout)
      }
    }
  }, [passwordData.newPassword])

  // OTP verification functions
  const sendOtpVerification = async (contactType: 'email' | 'phone', contactValue: string) => {
    setSendingOtp(true)
    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}')
          return tokens.access_token
        } catch {
          return null
        }
      })()

      if (!token) {
        throw new Error('Authentication required')
      }

      const normalizedContact = contactType === 'phone' ? normalizePhoneNumber(contactValue) : contactValue

      const response = await fetch('/api/v1/auth/send-verification-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contact: normalizedContact,
          purpose: 'profile_update',
          contact_type: contactType
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send verification code')
      }

      setResendCooldown(60)
      return true
    } catch (error) {
      console.error('Send OTP error:', error)
      setFieldErrors({ [contactType]: error instanceof Error ? error.message : 'Failed to send verification code' })
      return false
    } finally {
      setSendingOtp(false)
    }
  }

  const verifyOtpAndUpdate = async (contactType: 'email' | 'phone', contactValue: string, otpCode: string) => {
    setVerifyingOtp(true)
    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}')
          return tokens.access_token
        } catch {
          return null
        }
      })()

      if (!token) {
        throw new Error('Authentication required')
      }

      const normalizedContact = contactType === 'phone' ? normalizePhoneNumber(contactValue) : contactValue

      const response = await fetch('/api/v1/auth/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contact: normalizedContact,
          otp: otpCode,
          type: contactType,
          purpose: 'profile_update'
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Invalid verification code')
      }

      // Verification successful - now update the profile with the verified contact info
      const updateResponse = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          [contactType]: normalizedContact,
          otp_verified: true
        })
      })

      const updateData = await updateResponse.json()

      if (!updateData.success) {
        throw new Error(updateData.error || `Failed to update ${contactType}`)
      }

      // Close modal and refresh data
      closeOtpModal()
      setEditingField(null)
      await fetchUserProfile()

      return true
    } catch (error) {
      console.error('Verify OTP error:', error)
      setFieldErrors({ otp: error instanceof Error ? error.message : 'Verification failed' })
      return false
    } finally {
      setVerifyingOtp(false)
    }
  }

  // Individual field update handler
  const handleFieldUpdate = async (fieldName: string, value: string) => {
    setSavingPersonal(true)
    setFieldErrors({})

    try {
      // For email and phone changes, validate first
      if (fieldName === 'email' && value !== user?.email) {
        if (emailValidation.isValid === false) {
          setFieldErrors({ [fieldName]: emailValidation.error || 'Email validation failed' })
          return
        }

        if (emailValidation.isValid === null || emailValidation.isChecking) {
          setFieldErrors({ [fieldName]: 'Please wait for email validation to complete' })
          return
        }

        // Set up OTP verification
        setOtpType('email')
        setOtpModalMode('profile_update')
        setPendingMfaOtpMethod(null)
        setNewContactValue(value)
        const otpSent = await sendOtpVerification('email', value)
        if (otpSent) {
          setShowOtpModal(true)
        }
        return
      }

      if (fieldName === 'phone' && value !== (user && 'phone' in user ? user.phone : '')) {
        const normalizedPhone = normalizePhoneNumber(value)

        if (phoneValidation.isValid === false) {
          setFieldErrors({ [fieldName]: phoneValidation.error || 'Phone validation failed' })
          return
        }

        if (phoneValidation.isValid === null || phoneValidation.isChecking) {
          setFieldErrors({ [fieldName]: 'Please wait for phone validation to complete' })
          return
        }

        // Set up OTP verification
        setOtpType('phone')
        setOtpModalMode('profile_update')
        setPendingMfaOtpMethod(null)
        setNewContactValue(normalizedPhone)
        const otpSent = await sendOtpVerification('phone', normalizedPhone)
        if (otpSent) {
          setShowOtpModal(true)
        }
        return
      }

      // For other fields, update directly
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      if (!token) {
        setFieldErrors({ [fieldName]: 'Authentication required' })
        return
      }

      const updateData: Record<string, string> = { [fieldName]: value }

      const response = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      })

      const data = await response.json()

      if (data.success) {
        setEditingField(null)
        // Refresh user profile data
        await fetchUserProfile()
      } else {
        setFieldErrors({ [fieldName]: data.error || `Failed to update ${fieldName}` })
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error)
      setFieldErrors({ [fieldName]: `Failed to update ${fieldName}` })
    } finally {
      setSavingPersonal(false)
    }
  }

  // Avatar Upload Handler
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    await uploadAvatar(file)
  }

  // Camera Capture Handler
  const handleCameraCapture = async () => {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setAvatarError('Camera access is not supported in your browser')
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false
      })

      // Create a video element to show camera feed
      const video = document.createElement('video')
      video.srcObject = stream
      video.autoplay = true
      video.playsInline = true

      // Create canvas to capture the image
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      // Create a modal for camera preview
      const modal = document.createElement('div')
      modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.9);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      `

      video.style.cssText = `
        max-width: 90%;
        max-height: 70vh;
        border-radius: 8px;
      `

      const buttonContainer = document.createElement('div')
      buttonContainer.style.cssText = `
        margin-top: 20px;
        display: flex;
        gap: 12px;
      `

      const captureBtn = document.createElement('button')
      captureBtn.textContent = '📸 Capture Photo'
      captureBtn.style.cssText = `
        padding: 12px 24px;
        background: #0d9488;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 16px;
        cursor: pointer;
        font-weight: 500;
      `
      captureBtn.onmouseover = () => captureBtn.style.background = '#0f766e'
      captureBtn.onmouseout = () => captureBtn.style.background = '#0d9488'

      const cancelBtn = document.createElement('button')
      cancelBtn.textContent = '❌ Cancel'
      cancelBtn.style.cssText = `
        padding: 12px 24px;
        background: #6b7280;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 16px;
        cursor: pointer;
        font-weight: 500;
      `
      cancelBtn.onmouseover = () => cancelBtn.style.background = '#4b5563'
      cancelBtn.onmouseout = () => cancelBtn.style.background = '#6b7280'

      buttonContainer.appendChild(captureBtn)
      buttonContainer.appendChild(cancelBtn)
      modal.appendChild(video)
      modal.appendChild(buttonContainer)
      document.body.appendChild(modal)

      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop())
        document.body.removeChild(modal)
      }

      captureBtn.onclick = async () => {
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        // Draw video frame to canvas
        ctx?.drawImage(video, 0, 0)

        // Convert canvas to blob
        canvas.toBlob(async (blob) => {
          if (blob) {
            // Create a File object from the blob
            const file = new File([blob], 'avatar-capture.jpg', { type: 'image/jpeg' })
            cleanup()
            await uploadAvatar(file)
          }
        }, 'image/jpeg', 0.9)
      }

      cancelBtn.onclick = cleanup
    } catch (error) {
      console.error('Camera capture error:', error)
      setAvatarError('Failed to access camera. Please check permissions.')
    }
  }

  // Preferences submit handler
  const handlePreferencesSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingPreferences(true)
    setPreferencesErrors({})
    setPreferencesSuccess('')

    try {
      await apiPatch('/api/v1/preferences', {
        key: 'quietHours',
        value: {
          enabled: preferences.quietHoursEnabled,
          start: preferences.quietHoursStart,
          end: preferences.quietHoursEnd
        }
      })
      await apiPatch('/api/v1/preferences', {
        key: 'emailNotifications',
        value: preferences.emailNotifications
      })
      // Also save snake_case key for watcherNotificationService compatibility
      await apiPatch('/api/v1/preferences', {
        key: 'email_notifications',
        value: preferences.emailNotifications
      })
      await apiPatch('/api/v1/preferences', {
        key: 'smsNotifications',
        value: preferences.smsNotifications
      })
      await apiPatch('/api/v1/preferences', {
        key: 'marketingEmails',
        value: preferences.marketingEmails
      })
      await apiPatch('/api/v1/preferences', {
        key: 'securityAlerts',
        value: preferences.securityAlerts
      })
      setPreferencesSuccess('Preferences updated successfully!')
    } catch (error) {
      setPreferencesErrors({ submit: 'Failed to update preferences' })
    } finally {
      setSavingPreferences(false)
    }
  }

  // Security Handlers

  // Biometric unlock toggle handler
  const handleBiometricToggle = async () => {
    if (biometricLoading) return
    setBiometricLoading(true)

    try {
      const { biometricAuth } = await import('@/plugins/BiometricAuth')

      if (biometricEnabled) {
        // Turning off - delete stored credentials and disable
        await biometricAuth.deleteCredentials()
        biometricAuth.setEnabled(false)
        setBiometricEnabled(false)
        notify.success('Biometric unlock disabled')
      } else {
        // Turning on - verify biometric first, then enable
        const verified = await biometricAuth.verify({
          reason: 'Verify your identity to enable biometric unlock',
          title: 'Enable Biometric Unlock',
          description: 'Authenticate to enable fingerprint/face unlock for TotalAccess',
        })

        if (verified) {
          // Store a marker credential so we know biometric is set up
          // The actual auth tokens are handled by the lock screen flow
          const userId = authUser?.id?.toString() || 'user'
          await biometricAuth.setCredentials(userId, 'biometric_enabled')
          biometricAuth.setEnabled(true)
          setBiometricEnabled(true)
          notify.success('Biometric unlock enabled')
        } else {
          notify.error('Biometric verification failed or cancelled')
        }
      }
    } catch (error: unknown) {
      console.error('[Profile] Biometric toggle error:', error)
      notify.error(getErrorMessage(error, 'Failed to update biometric setting'))
    } finally {
      setBiometricLoading(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setChangingPassword(true)
    setPasswordErrors({})
    setPasswordSuccess('')

    if (!passwordValidation.isValid) {
      setPasswordErrors({ newPassword: passwordValidation.message || 'Password does not meet requirements' })
      setChangingPassword(false)
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordErrors({ confirmPassword: 'Passwords do not match' })
      setChangingPassword(false)
      return
    }

    try {
      const response = await apiPatch('/api/v1/user/profile', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setPasswordErrors({ submit: data.error || 'Failed to change password' })
        return
      }
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setPasswordSuccess('Password changed successfully!')
    } catch (error) {
      setPasswordErrors({ submit: 'Failed to change password' })
    } finally {
      setChangingPassword(false)
    }
  }

  // Account Linking Handlers
  const handleLinkAccount = async (provider: 'google' | 'microsoft' | 'intuit' | 'xero') => {
    setLinkingAccount(true)
    setLinkingError('')
    setLinkErrorMsg(null)

    try {
      if (!userDetails.id) {
        throw new Error('User not loaded')
      }

      // Get the auth token
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      if (!token) {
        throw new Error('Not authenticated')
      }

      // Build the OAuth URL with link_mode
      const currentUrl = window.location.origin
      const redirectUrl = encodeURIComponent(`${currentUrl}/client-zone/profile`)
      const clientType = Capacitor.isNativePlatform() ? 'mobile' : 'web'

      // Use the runtime-configured portal base so staging/prod builds share the same artifact safely
      const oauthBaseUrl = getRuntimePublicBaseUrl()

      const oauthUrl = `${oauthBaseUrl}/api/v1/auth/oauth/${provider}?` +
        `redirect_url=${redirectUrl}&` +
        `client_type=${clientType}&` +
        `link_mode=true&` +
        `user_id=${userDetails.id}`

      console.log(`[Profile] Initiating OAuth link for ${provider}:`, oauthUrl)

      // Redirect to OAuth provider
      window.location.href = oauthUrl

    } catch (error: unknown) {
      console.error(`[Profile] Error linking ${provider}:`, error)
      setLinkingError(getErrorMessage(error, `Failed to link ${provider} account`))
      setLinkingAccount(false)
    }
  }

  const handleUnlinkAccount = async (provider: 'google' | 'microsoft' | 'intuit' | 'xero') => {
    setLinkingAccount(true)
    setLinkingError('')
    setLinkErrorMsg(null)

    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      if (!token) {
        throw new Error('Not authenticated')
      }

      // Get the API endpoint URL - use Fastify API for unlink
      const apiBaseUrl = getEndpointUrl().replace('/api/v1', '').replace('/api', '') ||
        (process.env.NODE_ENV === 'production' ? 'https://api.totalaccess.co.za' : 'http://localhost:4001')

      const response = await fetch(`${apiBaseUrl}/api/v1/auth/oauth-unlink`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ provider })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || `Failed to unlink ${provider} account`)
      }

      // Update user state to remove provider ID
      if (provider === 'google') {
        setUserDetails(prev => ({ ...prev, google_id: null }))
      } else if (provider === 'microsoft') {
        setUserDetails(prev => ({ ...prev, microsoft_id: null }))
      } else if (provider === 'intuit') {
        setUserDetails(prev => ({ ...prev, intuit_id: null }))
      } else if (provider === 'xero') {
        setUserDetails(prev => ({ ...prev, xero_id: null }))
      }

      setLinkSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} account successfully unlinked!`)
      setTimeout(() => setLinkSuccess(null), 5000)

      // Refresh user profile
      fetchUserProfile()
    } catch (error: unknown) {
      console.error(`Error unlinking ${provider}:`, error)
      const message = getErrorMessage(error, `Failed to unlink ${provider} account`)
      setLinkingError(message)
      setLinkErrorMsg(message)
      setTimeout(() => {
        setLinkingError('')
        setLinkErrorMsg(null)
      }, 5000)
    } finally {
      setLinkingAccount(false)
    }
  }

  // MFA Functions
  const loadMFASettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const otpMethodsResponse = await fetch('/api/v1/auth/mfa/settings', { headers })
      if (otpMethodsResponse.ok) {
        const otpMethodsData = await otpMethodsResponse.json()
        const nextEmailOtpStatus = otpMethodsData.methods?.email_otp ?? null
        const nextWhatsappOtpStatus = otpMethodsData.methods?.whatsapp_otp ?? null
        const nextInAppApprovalStatus = otpMethodsData.methods?.inapp_approval ?? null
        const nextWebauthnMfaStatus = otpMethodsData.methods?.webauthn ?? null

        setEmailOtpStatus(nextEmailOtpStatus)
        setWhatsappOtpStatus(nextWhatsappOtpStatus)
        setInAppApprovalStatus(nextInAppApprovalStatus)
        setWebauthnMfaStatus(nextWebauthnMfaStatus)
        setEmailOtpEnabled(!!nextEmailOtpStatus?.enabled)
        setWhatsappOtpEnabled(!!nextWhatsappOtpStatus?.enabled)
        setInAppApprovalEnabled(!!nextInAppApprovalStatus?.enabled)
        setWebauthnMfaEnabled(!!nextWebauthnMfaStatus?.enabled)
      }

      // Load TOTP status
      const totpResponse = await fetch('/api/v1/auth/totp/status', { headers })
      if (totpResponse.ok) {
        const totpData = await totpResponse.json()
        console.log('[MFA] TOTP status loaded:', totpData)
        console.log('[MFA] TOTP enabled value:', totpData.enabled)

        // Workaround for backend API inconsistency: if setupDate exists but enabled is false,
        // infer that TOTP is actually enabled based on the presence of setupDate and backupCodes
        const actualEnabled = totpData.enabled || (totpData.setupDate && totpData.backupCodesRemaining !== undefined)
        console.log('[MFA] TOTP inferred enabled value:', actualEnabled)
        setTotpEnabled(actualEnabled)
      } else {
        console.error('[MFA] Failed to load TOTP status:', totpResponse.status)
        const errorData = await totpResponse.json().catch(() => ({}))
        console.error('[MFA] TOTP status error:', errorData)
      }

      // Load WebAuthn credentials only where passkeys are supported
      if (passkeysSupportedOnThisDevice) {
        const webauthnResponse = await fetch('/api/v1/auth/webauthn/credentials', { headers })
        if (webauthnResponse.ok) {
          const data = await webauthnResponse.json()
          setWebauthnCredentials(data.credentials || [])
        }
      } else {
        setWebauthnCredentials([])
      }

      // Load PIN status
      try {
        const pinResponse = await fetch('/api/v1/auth/pin/status', { headers })
        if (pinResponse.ok) {
          const pinStatusData = await pinResponse.json()
          setHasPin(pinStatusData.hasPin || false)
        }
      } catch (_e) {
        // Non-critical
      }
    } catch (error) {
      console.error('MFA settings load error:', error)
    }
  }, [passkeysSupportedOnThisDevice])

  const toggleOtpMethod = async (method: 'email_otp' | 'whatsapp_otp' | 'inapp_approval' | 'webauthn', enabled: boolean) => {
    try {
      setOtpMethodLoading(method)
      setTotpError('')
      setTotpSuccess('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (enabled && method === 'email_otp') {
        if (!emailOtpStatus?.available) {
          throw new Error('Add an email address before enabling email backup codes')
        }

        if (!emailOtpStatus?.verified) {
          throw new Error('Verify your email address before enabling email backup codes')
        }
      }

      if (enabled && method === 'whatsapp_otp') {
        if (!whatsappOtpStatus?.available) {
          throw new Error('Add a phone number in your profile before enabling phone backup codes')
        }

        if (!whatsappOtpStatus?.verified) {
          throw new Error('Verify your phone number before enabling phone backup codes')
        }
      }

      if (enabled && method === 'inapp_approval') {
        if (!inAppApprovalStatus?.available) {
          throw new Error('Sign in on the TotalAccess mobile app and enable push notifications before enabling in-app approval')
        }

        const sendApprovalResponse = await fetch('/api/v1/auth/mfa/settings', {
          method: 'POST',
          headers,
          body: JSON.stringify({ method, enabled: true, action: 'send_enable_approval' })
        })

        const sendApprovalData = await sendApprovalResponse.json()

        if (!sendApprovalResponse.ok) {
          throw new Error(sendApprovalData.error || 'Failed to send in-app approval request')
        }

        setPendingInAppApprovalSessionId(sendApprovalData.approval_session_id || null)
        setPendingInAppApprovalExpiresAt(sendApprovalData.expires_at || null)
        setPendingInAppApprovalState('pending')
        setShowInAppApprovalModal(true)
        setTotpSuccess(sendApprovalData.message || 'Approval request sent to your signed-in mobile app')
        return
      }

      if (enabled && method === 'webauthn') {
        const response = await fetch('/api/v1/auth/mfa/settings', {
          method: 'POST',
          headers,
          body: JSON.stringify({ method, enabled: true })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to enable passkey MFA')
        }

        setWebauthnMfaEnabled(true)
        setTotpSuccess(data.message || 'Passkey MFA enabled successfully')
        await loadMFASettings()
        return
      }

      if (enabled && (method === 'email_otp' || method === 'whatsapp_otp')) {
        const sendResponse = await fetch('/api/v1/auth/mfa/settings', {
          method: 'POST',
          headers,
          body: JSON.stringify({ method, enabled: true, action: 'send_enable_otp' })
        })

        const sendData = await sendResponse.json()

        if (!sendResponse.ok) {
          throw new Error(sendData.error || 'Failed to send verification code')
        }

        setOtpModalMode('mfa_enable')
        setPendingMfaOtpMethod(method)
        setOtpType(method === 'email_otp' ? 'email' : 'phone')
        setNewContactValue(sendData.contact || (method === 'email_otp' ? emailOtpStatus?.contact || '' : whatsappOtpStatus?.contact || ''))
        setVerificationCode(['', '', '', '', '', ''])
        setFieldErrors({})
        setResendCooldown(60)
        setShowOtpModal(true)
        setTotpSuccess(sendData.message || 'Verification code sent successfully')
        return
      }

      const response = await fetch('/api/v1/auth/mfa/settings', {
        method: 'POST',
        headers,
        body: JSON.stringify({ method, enabled })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update MFA method')
      }

      if (method === 'email_otp') {
        setEmailOtpEnabled(enabled)
      } else if (method === 'whatsapp_otp') {
        setWhatsappOtpEnabled(enabled)
      } else if (method === 'webauthn') {
        setWebauthnMfaEnabled(enabled)
      } else {
        setInAppApprovalEnabled(enabled)
      }

      setTotpSuccess(data.message || 'MFA method updated successfully')
      await loadMFASettings()
    } catch (error: unknown) {
      setTotpError(getErrorMessage(error, 'Failed to update MFA method'))
    } finally {
      setOtpMethodLoading(null)
    }
  }

  function closeOtpModal() {
    setShowOtpModal(false)
    setOtpType(null)
    setOtpModalMode(null)
    setPendingMfaOtpMethod(null)
    setNewContactValue('')
    setVerificationCode(['', '', '', '', '', ''])
    setFieldErrors({})
  }

  const closeInAppApprovalModal = useCallback(() => {
    setShowInAppApprovalModal(false)
    setPendingInAppApprovalSessionId(null)
    setPendingInAppApprovalExpiresAt(null)
    setPendingInAppApprovalState(null)
  }, [])

  const verifyMfaEnableOtp = async (method: 'email_otp' | 'whatsapp_otp', otpCode: string) => {
    setVerifyingOtp(true)
    try {
      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch('/api/v1/auth/mfa/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ method, enabled: true, action: 'verify_enable_otp', code: otpCode })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to enable MFA method')
      }

      if (method === 'email_otp') {
        setEmailOtpEnabled(true)
      } else {
        setWhatsappOtpEnabled(true)
      }

      closeOtpModal()
      setTotpSuccess(data.message || 'MFA method enabled successfully')
      await loadMFASettings()
      return true
    } catch (error) {
      setFieldErrors({ otp: error instanceof Error ? error.message : 'Failed to enable MFA method' })
      return false
    } finally {
      setVerifyingOtp(false)
    }
  }

  const whatsappOtpUnavailableReason = useMemo(() => {
    if (whatsappOtpEnabled) {
      return null
    }

    if (!whatsappOtpStatus?.available) {
      return 'Add and save a phone number in your profile to use phone backup codes.'
    }

    if (!whatsappOtpStatus?.verified) {
      return 'Verify your saved phone number before enabling phone backup codes.'
    }

    if (whatsappOtpStatus?.eligibilityError) {
      return `${whatsappOtpStatus.eligibilityError} We will still fall back to SMS when WhatsApp is unavailable.`
    }

    if (whatsappOtpStatus?.eligibilityChecked && whatsappOtpStatus?.hasWhatsApp === false) {
      return 'This number is not on WhatsApp right now, so TotalAccess will send the backup code by SMS instead.'
    }

    return null
  }, [whatsappOtpEnabled, whatsappOtpStatus])

  useEffect(() => {
    if (!showInAppApprovalModal || !pendingInAppApprovalSessionId || pendingInAppApprovalState !== 'pending') {
      return
    }

    let cancelled = false

    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('token') || (() => {
          try {
            const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
            return tokens.access_token;
          } catch {
            return null;
          }
        })();

        const response = await fetch('/api/v1/auth/mfa/settings', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            method: 'inapp_approval',
            enabled: true,
            action: 'status_enable_approval',
            approval_session_id: pendingInAppApprovalSessionId,
          })
        })

        const data = await response.json()
        if (cancelled) {
          return
        }

        if (response.ok && data.approval_status === 'approved') {
          setPendingInAppApprovalState('approved')
          setInAppApprovalEnabled(true)
          setShowInAppApprovalModal(false)
          setTotpSuccess(data.message || 'In-app approval enabled successfully!')
          await loadMFASettings()
          closeInAppApprovalModal()
          return
        }

        if (response.status === 409 || data.approval_status === 'denied') {
          setPendingInAppApprovalState('denied')
          setTotpError(data.message || 'The in-app approval request was denied.')
          closeInAppApprovalModal()
          return
        }

        if (response.status === 410 || data.approval_status === 'expired') {
          setPendingInAppApprovalState('expired')
          setTotpError(data.message || 'The in-app approval request expired. Please try again.')
          closeInAppApprovalModal()
          return
        }

        if (!response.ok) {
          throw new Error(data.error || 'Failed to check in-app approval status')
        }

        setPendingInAppApprovalExpiresAt(data.expires_at || null)
      } catch (error) {
        if (!cancelled) {
          setTotpError(error instanceof Error ? error.message : 'Failed to check in-app approval status')
          closeInAppApprovalModal()
        }
      }
    }

    void pollStatus()
    const intervalId = window.setInterval(() => {
      void pollStatus()
    }, 2500)

    return () => {
      cancelled = true
      window.clearInterval(intervalId)
    }
  }, [closeInAppApprovalModal, loadMFASettings, pendingInAppApprovalSessionId, pendingInAppApprovalState, showInAppApprovalModal])

  const inAppApprovalUnavailableReason = useMemo(() => {
    if (inAppApprovalEnabled) {
      return null
    }

    if (!inAppApprovalStatus?.available) {
      return 'Sign in on the TotalAccess mobile app and keep push notifications enabled to use in-app approval.'
    }

    if ((inAppApprovalStatus?.activeDeviceCount || 0) <= 0) {
      return 'No signed-in mobile device is currently ready to receive approval prompts.'
    }

    return null
  }, [inAppApprovalEnabled, inAppApprovalStatus])

  const emailOtpUnavailableReason = useMemo(() => {
    if (emailOtpEnabled) {
      return null
    }

    if (!emailOtpStatus?.available) {
      return 'Add and save an email address in your profile to use email backup codes.'
    }

    if (!emailOtpStatus?.verified) {
      return 'Verify your saved email address before enabling email backup codes.'
    }

    return null
  }, [emailOtpEnabled, emailOtpStatus])

  const automaticBackupMethodsEnabled = useMemo(
    () => Boolean(emailOtpStatus?.isAutomatic || whatsappOtpStatus?.isAutomatic),
    [emailOtpStatus?.isAutomatic, whatsappOtpStatus?.isAutomatic]
  )

  const automaticBackupReadinessMessage = useMemo(() => {
    if (automaticBackupMethodsEnabled) {
      return 'These backup codes are enabled automatically whenever MFA is on for your account.'
    }

    if (emailOtpStatus?.available && emailOtpStatus?.verified) {
      return 'Once you turn on Passkeys, Authenticator, or In-App Approval, verified email and phone backups switch on automatically.'
    }

    return 'Add and verify your email or phone details to have automatic backup codes ready when MFA is enabled.'
  }, [automaticBackupMethodsEnabled, emailOtpStatus?.available, emailOtpStatus?.verified])

  const mfaPreferenceOptions = useMemo(() => {
    const options = [
      {
        method: 'inapp_approval' as const,
        label: 'In-App Approval',
        description: 'Approve sign-ins from your TotalAccess mobile app.',
        enabled: inAppApprovalEnabled,
        canSetPreferred: inAppApprovalEnabled,
        isPrimary: Boolean(inAppApprovalStatus?.isPrimary),
      },
      {
        method: 'webauthn' as const,
        label: 'Passkey',
        description: 'Use biometrics or a security key.',
        enabled: webauthnMfaEnabled,
        canSetPreferred: webauthnMfaEnabled,
        isPrimary: Boolean(webauthnMfaStatus?.isPrimary),
      },
      {
        method: 'email_otp' as const,
        label: 'Email OTP',
        description: 'Receive one-time codes at your verified email address.',
        enabled: emailOtpEnabled,
        canSetPreferred: emailOtpEnabled && !emailOtpStatus?.isAutomatic,
        isPrimary: Boolean(emailOtpStatus?.isPrimary),
      },
      {
        method: 'whatsapp_otp' as const,
        label: 'Phone OTP',
        description: 'Receive one-time codes on your verified phone.',
        enabled: whatsappOtpEnabled,
        canSetPreferred: whatsappOtpEnabled && !whatsappOtpStatus?.isAutomatic,
        isPrimary: Boolean(whatsappOtpStatus?.isPrimary),
      },
    ]

    return options.filter((option) => option.enabled || option.isPrimary)
  }, [
    emailOtpEnabled,
    emailOtpStatus?.isPrimary,
    emailOtpStatus?.isAutomatic,
    inAppApprovalEnabled,
    inAppApprovalStatus?.isPrimary,
    webauthnMfaEnabled,
    webauthnMfaStatus?.isPrimary,
    whatsappOtpEnabled,
    whatsappOtpStatus?.isAutomatic,
    whatsappOtpStatus?.isPrimary,
  ])

  const preferredMfaMethod = useMemo(
    () => mfaPreferenceOptions.find((option) => option.isPrimary && option.enabled && option.canSetPreferred)
      || mfaPreferenceOptions.find((option) => option.enabled && option.canSetPreferred)
      || mfaPreferenceOptions.find((option) => option.enabled)
      || null,
    [mfaPreferenceOptions]
  )

  const phoneBackupDeliveryHint = useMemo(() => {
    if (!whatsappOtpStatus?.available) {
      return null
    }

    if (whatsappOtpStatus?.eligibilityChecked && whatsappOtpStatus?.hasWhatsApp === true) {
      return 'Codes will go to WhatsApp first, with SMS fallback if needed.'
    }

    if (whatsappOtpStatus?.eligibilityChecked && whatsappOtpStatus?.hasWhatsApp === false) {
      return 'Codes will be sent by SMS because WhatsApp is not available on this number.'
    }

    return 'Codes will be sent to your verified phone using the best available channel.'
  }, [whatsappOtpStatus])

  const setPreferredMfaMethod = useCallback(async (method: ManagedMfaMethod) => {
    try {
      setPreferredMfaMethodLoading(method)
      setTotpError('')
      setTotpSuccess('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch('/api/v1/auth/mfa/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ method, enabled: true, action: 'set_primary' })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update preferred MFA method')
      }

      setTotpSuccess(data.message || 'Preferred MFA method updated successfully')
      await loadMFASettings()
    } catch (error: unknown) {
      setTotpError(getErrorMessage(error, 'Failed to update preferred MFA method'))
    } finally {
      setPreferredMfaMethodLoading(null)
    }
  }, [loadMFASettings])

  const startTotpSetup = async () => {
    try {
      setTotpLoading(true)
      setTotpError('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch('/api/v1/auth/totp/setup', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (!response.ok) {
        // If setup fails because TOTP is already enabled, refresh the status to sync state
        if (data.error && data.error.includes('already set up')) {
          console.log('[MFA] TOTP already enabled on backend, refreshing status...')
          await loadMFASettings()
          console.log('[MFA] TOTP status after refresh:', totpEnabled)
          // Update error message to be more helpful
          throw new Error('TOTP is already enabled on your account. Please use the "Disable" button below first, then try setting it up again.')
        }
        throw new Error(data.error || 'Failed to setup TOTP')
      }

      setTotpSetup(data.data)
      setShowTotpSetup(true)
    } catch (error: unknown) {
      setTotpError(getErrorMessage(error, 'Failed to setup TOTP'))
    } finally {
      setTotpLoading(false)
    }
  }

  const enableTotp = async () => {
    try {
      setTotpLoading(true)
      setTotpError('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch('/api/v1/auth/totp/enable', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          secret: totpSetup?.secret,
          code: totpVerificationCode
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to enable TOTP')
      }

      setBackupCodes(data.backupCodes || [])
      setShowBackupCodes(true)
      setShowTotpSetup(false)
      setTotpSuccess('Authenticator app enabled successfully!')
      setTotpEnabled(true)
      loadMFASettings()
    } catch (error: unknown) {
      setTotpError(getErrorMessage(error, 'Failed to enable TOTP'))
    } finally {
      setTotpLoading(false)
    }
  }

  const disableTotp = async () => {
    setShowTotpDisable(true)
    setTotpDisablePassword('')
    setTotpDisableCode('')
    setTotpError('')
  }

  const handleTotpDisable = async () => {
    if (!totpDisablePassword || !totpDisableCode) {
      setTotpError('Please enter both your password and TOTP code')
      return
    }

    if (!/^\d{6}$/.test(totpDisableCode)) {
      setTotpError('Please enter a valid 6-digit TOTP code')
      return
    }

    try {
      setTotpLoading(true)
      setTotpError('')
      console.log('[MFA] Calling TOTP disable API with password and code')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch('/api/v1/auth/totp/disable', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: totpDisablePassword, code: totpDisableCode })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disable TOTP')
      }

      setTotpSuccess('Authenticator app disabled successfully')
      setTotpEnabled(false)
      setShowTotpDisable(false)
      setTotpDisablePassword('')
      setTotpDisableCode('')
      loadMFASettings()
    } catch (error: unknown) {
      setTotpError(getErrorMessage(error, 'Failed to disable TOTP'))
    } finally {
      setTotpLoading(false)
    }
  }

  const regenerateBackupCodes = async () => {
    const password = await notify.prompt({
      title: 'Regenerate Backup Codes',
      message: 'Enter your password to regenerate backup codes:',
      inputType: 'password',
      confirmText: 'Generate',
      variant: 'warning',
      required: true
    })
    if (!password) return

    try {
      setTotpLoading(true)
      setTotpError('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch('/api/v1/auth/backup-codes/regenerate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to regenerate backup codes')
      }

      setBackupCodes(data.backupCodes || [])
      setShowBackupCodes(true)
      setTotpSuccess('Backup codes regenerated successfully!')
    } catch (error: unknown) {
      setTotpError(getErrorMessage(error, 'Failed to regenerate backup codes'))
    } finally {
      setTotpLoading(false)
    }
  }

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join('\\n')
    const blob = new Blob([codesText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'totalaccess-backup-codes.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const registerPasskey = async () => {
    try {
      if (!passkeysSupportedOnThisDevice) {
        throw new Error('Passkeys are only available in the portal browser. Use In-App Approval, Authenticator App, or backup codes on mobile and PWA.')
      }

      // If central passkey management is required, redirect to central portal
      if (requiresCentralPasskeyManagement && centralPasskeyOrigin) {
        const handoffState: CentralPasskeyRegistrationHandoffState = {
          returnOrigin: currentOrigin,
          sourceHostname: currentHostname,
          deviceName: deviceName || 'Unnamed Device',
        };

        const handoffUrl = buildCentralPasskeyRegistrationHandoffUrl({
          centralOrigin: centralPasskeyOrigin,
          state: handoffState,
        });

        window.location.href = handoffUrl;
        return;
      }

      setWebauthnLoading(true)
      setWebauthnError('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Get registration options from server
      const optionsResponse = await fetch('/api/v1/auth/webauthn/register/options', {
        method: 'POST',
        headers
      })

      const optionsData = await optionsResponse.json()

      if (!optionsResponse.ok) {
        throw new Error(optionsData.error || 'Failed to get registration options')
      }

      // Start WebAuthn registration
      const credential = await startRegistration(optionsData.options)

      // Verify registration on server
      const verifyResponse = await fetch('/api/v1/auth/webauthn/register/verify', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          credential,
          challenge: optionsData.options.challenge,
          deviceName: deviceName || 'Unnamed Device'
        })
      })

      const verifyData = await verifyResponse.json()

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Failed to verify passkey')
      }

      setWebauthnSuccess('Passkey registered successfully!')
      setDeviceName('')
      await loadMFASettings()
    } catch (error: unknown) {
      console.error('Passkey registration error:', error)
      setWebauthnError(getErrorMessage(error, 'Failed to register passkey'))
    } finally {
      setWebauthnLoading(false)
    }
  }

  const deletePasskey = async (credentialId: string) => {
    if (!(await confirm({ message: 'Are you sure you want to delete this passkey?', variant: 'danger' }))) {
      return
    }

    try {
      setWebauthnLoading(true)
      setWebauthnError('')

      const token = localStorage.getItem('token') || (() => {
        try {
          const tokens = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return tokens.access_token;
        } catch {
          return null;
        }
      })();

      const response = await fetch(`/api/v1/auth/webauthn/credentials?id=${credentialId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete passkey')
      }

      setWebauthnSuccess('Passkey deleted successfully')
      await loadMFASettings()
    } catch (error: unknown) {
      setWebauthnError(getErrorMessage(error, 'Failed to delete passkey'))
    } finally {
      setWebauthnLoading(false)
    }
  }

  // Lock Screen PIN Functions
  const openCentralPasskeyManagement = useCallback(() => {
    if (!centralPasskeyManagementUrl) {
      setWebauthnError('No shared passkey portal is configured for this environment.')
      return
    }

    window.location.href = centralPasskeyManagementUrl
  }, [centralPasskeyManagementUrl])

  const returnToSourcePortal = useCallback(() => {
    if (!centralPasskeyReturnUrl) {
      return
    }

    window.location.href = centralPasskeyReturnUrl.toString()
  }, [centralPasskeyReturnUrl])

  const handleSetPin = async () => {
    setPinError('')
    setPinSuccess('')

    if (!/^\d{6}$/.test(pinData.pin)) {
      setPinError('PIN must be exactly 6 digits')
      return
    }
    if (pinData.pin !== pinData.confirmPin) {
      setPinError('PINs do not match')
      return
    }
    if (!pinData.password) {
      setPinError('Password is required to set a PIN')
      return
    }

    try {
      setPinLoading(true)
      const response = await apiFetch('/api/v1/auth/pin/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pinData.password, pin: pinData.pin })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to set PIN')

      setPinSuccess(hasPin ? 'PIN updated successfully!' : 'Lock screen PIN set successfully!')
      setHasPin(true)
      setShowPinSetup(false)
      setPinData({ pin: '', confirmPin: '', password: '' })
      notify.success(hasPin ? 'PIN updated!' : 'Lock screen PIN set!')
    } catch (error: unknown) {
      setPinError(getErrorMessage(error, 'Failed to set PIN'))
    } finally {
      setPinLoading(false)
    }
  }

  const handleRemovePin = async () => {
    const password = await notify.prompt({
      title: 'Remove Lock Screen PIN',
      message: 'Enter your password to remove the lock screen PIN:',
      inputType: 'password',
      confirmText: 'Remove PIN',
      variant: 'danger',
      required: true
    })
    if (!password) return

    try {
      setPinLoading(true)
      setPinError('')
      const response = await apiFetch('/api/v1/auth/pin/set', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, pin: null })
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to remove PIN')

      setPinSuccess('Lock screen PIN removed')
      setHasPin(false)
      notify.success('Lock screen PIN removed')
    } catch (error: unknown) {
      setPinError(getErrorMessage(error, 'Failed to remove PIN'))
    } finally {
      setPinLoading(false)
    }
  }

  // Load MFA settings on mount
  useEffect(() => {
    if (isAuthenticated && activeTab === 'security') {
      void loadMFASettings()
    }
  }, [isAuthenticated, activeTab, loadMFASettings])

  // Handle return from central portal after passkey registration
  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const passkeyRegHandoff = searchParams.get('passkey_registration_handoff')
    if (passkeyRegHandoff === '1') {
      const handoffState = readCentralPasskeyRegistrationHandoffStateFromHash(window.location.hash)
      if (handoffState?.returnOrigin) {
        setWebauthnSuccess('Passkey registered successfully! Redirecting back...')
        setTimeout(() => {
          window.location.href = handoffState.returnOrigin
        }, 1500)
      }
      // Clean up URL
      router.replace('/client-zone/profile?tab=security', { scroll: false })
    }
  }, [searchParams, router])

  const tabs = [
    { id: 'personal' as TabType, name: 'Personal', icon: '📝' },
    { id: 'security' as TabType, name: 'Security', icon: '🔒' },
    { id: 'notifications' as TabType, name: 'Notifications', icon: '🔔' },
    // { id: 'contacts' as TabType, name: 'Contacts', icon: '👥' },
    { id: 'terms' as TabType, name: 'Terms & Privacy', icon: '📋' },
    { id: 'overview' as TabType, name: 'Organizations', icon: '🏢' }
  ]

  // OTP modal handlers
  const handleOtpCodeChange = (index: number, value: string) => {
    if (value.length > 1) return

    const newCode = [...verificationCode]
    newCode[index] = value
    setVerificationCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      nextInput?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return

    if (otpModalMode === 'mfa_enable' && pendingMfaOtpMethod) {
      await toggleOtpMethod(pendingMfaOtpMethod, true)
      return
    }

    if (!otpType || !newContactValue) return

    const success = await sendOtpVerification(otpType, newContactValue)
    if (!success) {
      // Error handling is done in sendOtpVerification
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    const otpCode = verificationCode.join('')

    if (otpCode.length !== 6) {
      setFieldErrors({ otp: 'Please enter the complete verification code' })
      return
    }

    if (otpModalMode === 'mfa_enable' && pendingMfaOtpMethod) {
      await verifyMfaEnableOtp(pendingMfaOtpMethod, otpCode)
      return
    }

    if (!otpType || !newContactValue) {
      setFieldErrors({ otp: 'Missing verification data' })
      return
    }

    const success = await verifyOtpAndUpdate(otpType, newContactValue, otpCode)
    if (!success) {
      // Error handling is done in verifyOtpAndUpdate
    }
  }

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      // If we're on the profile page (e.g. returning from OAuth link flow),
      // redirect to dashboard instead of going back to the OAuth redirect URL
      if (typeof window !== 'undefined' && window.location.pathname.includes('/client-zone/profile')) {
        router.replace('/client-zone/dashboard')
      } else {
        router.back()
      }
    }
  }, [onClose, router])

  if (!isOpen) return null

  if (loading) {
    return (
      <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <BrandedLoadingSpinner mode="compact" size="lg" showSurface={false} showProgressBar={false} />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto" data-tour-id="profile-modal" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="flex min-h-full flex-col px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        {/* Header */}
        <Header onClose={handleClose} />

        <div className="relative flex-1">
        {/* Tab Navigation */}
        <div className="mb-4 px-2">
          <TabNav tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-3 sm:p-4 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <OverviewTab
                clientMemberships={clientMemberships}
                pendingRequests={pendingRequests}
                availableInvitations={availableInvitations}
                loadingClients={loadingClients}
                currentClientId={navigationData?.client_info?.id}
                onOpenOnboarding={() => setShowOnboardingModal(true)}
              />
            )}

            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <div>
                <h3 className="text-lg font-semibold pm-text-primary mb-4">Personal Information</h3>

                {/* Avatar Upload Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Profile Picture
                  </label>
                  <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
                    <div className="relative flex-shrink-0">
                      {userDetails.avatar_url ? (
                        <Image
                          src={userDetails.avatar_url}
                          alt="Profile Picture"
                          width={80}
                          height={80}
                          className="rounded-full object-cover border-2 border-gray-300 dark:border-gray-600"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                          <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                          </svg>
                        </div>
                      )}
                      {/* Always visible edit button */}
                      <button
                        type="button"
                        onClick={() => setShowAvatarOptions(!showAvatarOptions)}
                        className="absolute bottom-0 right-0 bg-[var(--theme-primary)] text-white rounded-full p-1.5 cursor-pointer hover:bg-[var(--theme-primary-dark)] transition-colors shadow-lg"
                        title="Change profile picture"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>

                    <div className="flex-1 w-full text-center sm:text-left">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                        Upload a profile picture. Maximum file size: 5MB. Supported formats: JPG, PNG, GIF, WebP.
                      </p>

                      {/* Upload/Camera Options */}
                      {showAvatarOptions && !uploadingAvatar && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          <label className="inline-flex items-center px-4 py-2 bg-[var(--theme-primary)] text-white text-sm rounded-lg hover:bg-[var(--theme-primary-dark)] transition-colors cursor-pointer">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Upload Photo
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleAvatarUpload}
                              className="hidden"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={handleCameraCapture}
                            className="inline-flex items-center px-4 py-2 bg-[var(--theme-primary)] text-white text-sm rounded-lg hover:bg-[var(--theme-primary-dark)] transition-colors"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Use Camera
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowAvatarOptions(false)}
                            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      {/* Status Messages */}
                      {uploadingAvatar && (
                        <div className="flex items-center text-sm text-[var(--theme-primary)]">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--theme-primary)] mr-2"></div>
                          Uploading...
                        </div>
                      )}
                      {avatarError && (
                        <p className="text-sm text-red-600">{avatarError}</p>
                      )}
                      {avatarSuccess && (
                        <p className="text-sm text-green-600">{avatarSuccess}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Individual Field Components */}
                <div className="space-y-6">
                  {/* First Name and Last Name */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <EditableField
                      label="First Name"
                      value={userDetails.first_name}
                      fieldName="first_name"
                      isEditing={editingField === 'first_name'}
                      onEdit={() => setEditingField('first_name')}
                      onSave={(value) => handleFieldUpdate('first_name', value)}
                      onCancel={() => setEditingField(null)}
                      saving={savingPersonal}
                      error={fieldErrors.first_name}
                    />
                    <EditableField
                      label="Last Name"
                      value={userDetails.last_name}
                      fieldName="last_name"
                      isEditing={editingField === 'last_name'}
                      onEdit={() => setEditingField('last_name')}
                      onSave={(value) => handleFieldUpdate('last_name', value)}
                      onCancel={() => setEditingField(null)}
                      saving={savingPersonal}
                      error={fieldErrors.last_name}
                    />
                  </div>

                  {/* Title and Date of Birth */}
                  <div className="grid gap-6 md:grid-cols-2">
                    <EditableSelectField
                      label="Title"
                      value={userDetails.title}
                      fieldName="title"
                      options={[
                        { value: '', label: 'Select Title' },
                        { value: 'Mr', label: 'Mr' },
                        { value: 'Miss', label: 'Miss' },
                        { value: 'Ms', label: 'Ms' },
                        { value: 'Mrs', label: 'Mrs' },
                        { value: 'Dr', label: 'Dr' },
                        { value: 'Prof', label: 'Prof' }
                      ]}
                      isEditing={editingField === 'title'}
                      onEdit={() => setEditingField('title')}
                      onSave={(value) => handleFieldUpdate('title', value)}
                      onCancel={() => setEditingField(null)}
                      saving={savingPersonal}
                      error={fieldErrors.title}
                    />
                    <EditableField
                      label="Date of Birth"
                      value={userDetails.dob}
                      fieldName="dob"
                      type="date"
                      isEditing={editingField === 'dob'}
                      onEdit={() => setEditingField('dob')}
                      onSave={(value) => handleFieldUpdate('dob', value)}
                      onCancel={() => setEditingField(null)}
                      saving={savingPersonal}
                      error={fieldErrors.dob}
                    />
                  </div>

                  {/* ID Number */}
                  <EditableField
                    label="ID Number"
                    value={userDetails.id_number}
                    fieldName="id_number"
                    isEditing={editingField === 'id_number'}
                    onEdit={() => setEditingField('id_number')}
                    onSave={(value) => handleFieldUpdate('id_number', value)}
                    onCancel={() => setEditingField(null)}
                    saving={savingPersonal}
                    error={fieldErrors.id_number}
                  />

                  {/* Address */}
                  <EditableTextareaField
                    label="Address"
                    value={userDetails.address}
                    fieldName="address"
                    isEditing={editingField === 'address'}
                    onEdit={() => setEditingField('address')}
                    onSave={(value) => handleFieldUpdate('address', value)}
                    onCancel={() => setEditingField(null)}
                    saving={savingPersonal}
                    error={fieldErrors.address}
                  />

                  {/* LinkedIn Profile */}
                  <EditableField
                    label="LinkedIn Profile URL"
                    value={userDetails.linkedin_url}
                    fieldName="linkedin_url"
                    type="url"
                    placeholder="https://linkedin.com/in/yourprofile"
                    isEditing={editingField === 'linkedin_url'}
                    onEdit={() => setEditingField('linkedin_url')}
                    onSave={(value) => handleFieldUpdate('linkedin_url', value)}
                    onCancel={() => setEditingField(null)}
                    saving={savingPersonal}
                    error={fieldErrors.linkedin_url}
                  />

                  {/* Email */}
                  <EditableEmailField
                    label="Email (personal login identity)"
                    value={userDetails.email}
                    fieldName="email"
                    isEditing={editingField === 'email'}
                    onEdit={() => setEditingField('email')}
                    onSave={(value) => handleFieldUpdate('email', value)}
                    onCancel={() => setEditingField(null)}
                    saving={savingPersonal}
                    error={fieldErrors.email}
                    validation={emailValidation}
                    requiresVerification={userDetails.email !== (user && 'email' in user ? user.email : '')}
                    onValueChange={(value) => setUserDetails(prev => ({ ...prev, email: value }))}
                  />

                  {/* Phone */}
                  <EditablePhoneField
                    label="Phone (personal login identity)"
                    value={userDetails.phone}
                    fieldName="phone"
                    isEditing={editingField === 'phone'}
                    onEdit={() => setEditingField('phone')}
                    onSave={(value) => handleFieldUpdate('phone', value)}
                    onCancel={() => setEditingField(null)}
                    saving={savingPersonal}
                    error={fieldErrors.phone}
                    validation={phoneValidation}
                    requiresVerification={userDetails.phone !== (user && 'phone' in user ? user.phone : '')}
                    onValueChange={(value) => setUserDetails(prev => ({ ...prev, phone: value }))}
                  />

                  {/* Business contacts (read-only, client-managed) */}
                  {(userDetails.membership_business_email || userDetails.membership_business_phone) && (
                    <div className="mt-4 rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] p-3 dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.2)]">
                      <p className="mb-2 text-xs font-semibold text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                        Business Contact (managed by your organization)
                      </p>
                      <p className="mb-3 text-xs text-[var(--theme-primary)] dark:text-[var(--theme-accent)]">
                        These are company-issued contact details linked to your membership. They can be used for login but are managed by your organization — you cannot edit them here.
                      </p>
                      <div className="space-y-2">
                        {userDetails.membership_business_email && (
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Business Email</span>
                            <p className="text-sm text-gray-900 dark:text-gray-100">{userDetails.membership_business_email}</p>
                          </div>
                        )}
                        {userDetails.membership_business_phone && (
                          <div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Business Phone</span>
                            <p className="text-sm text-gray-900 dark:text-gray-100">{userDetails.membership_business_phone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div>
                <h3 className="text-lg font-semibold pm-text-primary mb-4">Security Settings</h3>

                {/* OAuth Linked Accounts */}
                <div className="bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                  <h4 className="text-md font-semibold pm-text-primary mb-2">OAuth Linked Accounts</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Connect social accounts for easy sign-in and enhanced security.
                  </p>

                  {/* Success Message */}
                  {linkSuccess && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
                      {linkSuccess}
                    </div>
                  )}

                  {/* Error Messages */}
                  {(linkingError || linkErrorMsg) && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                      {linkingError || linkErrorMsg}
                    </div>
                  )}

                  {/* OAuth Provider Cards */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {/* Google Account */}
                  <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" className="w-6 h-6">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h5 className="font-medium pm-text-primary">Google Account</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {userDetails.google_id ? 'Account linked' : 'Not linked'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {userDetails.google_id ? (
                          <button
                            onClick={() => handleUnlinkAccount('google')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            Unlink
                          </button>
                        ) : (
                          <button
                            onClick={() => handleLinkAccount('google')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50"
                          >
                            Link Account
                          </button>
                        )}
                      </div>
                    </div>
                    {userDetails.google_id && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
                          <span className="font-medium">Provider ID:</span> {userDetails.google_id}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Microsoft Account */}
                  <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" className="w-6 h-6">
                            <path fill="#F25022" d="M1 1h10v10H1z" />
                            <path fill="#00A4EF" d="M13 1h10v10H13z" />
                            <path fill="#7FBA00" d="M1 13h10v10H1z" />
                            <path fill="#FFB900" d="M13 13h10v10H13z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h5 className="font-medium pm-text-primary">Microsoft Account</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {userDetails.microsoft_id ? 'Account linked' : 'Not linked'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {userDetails.microsoft_id ? (
                          <button
                            onClick={() => handleUnlinkAccount('microsoft')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            Unlink
                          </button>
                        ) : (
                          <button
                            onClick={() => handleLinkAccount('microsoft')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50"
                          >
                            Link Account
                          </button>
                        )}
                      </div>
                    </div>
                    {userDetails.microsoft_id && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
                          <span className="font-medium">Provider ID:</span> {userDetails.microsoft_id}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Intuit / QuickBooks Account */}
                  <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <img src="/bank-logos/Intuit Quickbooks.svg" alt="QuickBooks" className="h-6 w-6 object-contain" />
                        </div>
                        <div className="min-w-0">
                          <h5 className="font-medium pm-text-primary">Intuit / QuickBooks</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {userDetails.intuit_id ? 'Account linked' : 'Not linked'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {userDetails.intuit_id ? (
                          <button
                            onClick={() => handleUnlinkAccount('intuit')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            Unlink
                          </button>
                        ) : (
                          <button
                            onClick={() => handleLinkAccount('intuit')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50"
                          >
                            Link Account
                          </button>
                        )}
                      </div>
                    </div>
                    {userDetails.intuit_id && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
                          <span className="font-medium">Provider ID:</span> {userDetails.intuit_id}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Xero Account */}
                  <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                          <img src="/bank-logos/xero.svg" alt="Xero" className="h-6 w-6 object-contain" />
                        </div>
                        <div className="min-w-0">
                          <h5 className="font-medium pm-text-primary">Xero</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {userDetails.xero_id ? 'Account linked' : 'Not linked'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {userDetails.xero_id ? (
                          <button
                            onClick={() => handleUnlinkAccount('xero')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                          >
                            Unlink
                          </button>
                        ) : (
                          <button
                            onClick={() => handleLinkAccount('xero')}
                            disabled={linkingAccount}
                            className="px-3 py-1 text-sm bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50"
                          >
                            Link Account
                          </button>
                        )}
                      </div>
                    </div>
                    {userDetails.xero_id && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-all">
                          <span className="font-medium">Provider ID:</span> {userDetails.xero_id}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                </div>

                {/* Multi-Factor Authentication */}
                <div className="bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                  <h4 className="text-md font-semibold pm-text-primary mb-2">Multi-Factor Authentication (MFA)</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Choose a sign-in method, then add a backup code method.
                  </p>
                  {isMobileMfaContext && (
                    <div className="mb-3 rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] px-3 py-2 text-sm text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:text-[var(--theme-accent)]">
                      Mobile: Use <strong>In-App Approval</strong> first, then <strong>Authenticator App</strong>.
                    </div>
                  )}
                  {passkeysSupportedOnThisDevice && passkeyScopeMessage && (
                    <div className="mb-3 rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] px-3 py-2 text-sm text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:text-[var(--theme-accent)]">
                      <strong>Passkey scope:</strong> {passkeyScopeMessage}
                    </div>
                  )}

                  {/* Error/Success Messages */}
                  {totpError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                      {totpError}
                    </div>
                  )}
                  {totpSuccess && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
                      {totpSuccess}
                    </div>
                  )}
                  {webauthnError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                      {webauthnError}
                    </div>
                  )}
                  {webauthnSuccess && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
                      {webauthnSuccess}
                    </div>
                  )}

                  <div className="mb-4 rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] p-4 text-sm text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:text-[var(--theme-accent)]">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">Preferred MFA order</p>
                        <p className="mt-1 text-sm leading-6 text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                          Your preferred MFA method is shown first during sign-in. Other enabled methods stay available as alternatives.
                        </p>
                        {preferredMfaMethod ? (
                          <p className="mt-2 text-xs font-medium text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                            Current primary: <span className="font-semibold">{preferredMfaMethod.label}</span>
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {mfaPreferenceOptions.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {mfaPreferenceOptions.map((option) => (
                          <button
                            key={option.method}
                            type="button"
                            onClick={() => void setPreferredMfaMethod(option.method)}
                            disabled={!option.enabled || !option.canSetPreferred || preferredMfaMethodLoading === option.method}
                            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition ${option.isPrimary
                              ? 'border-[rgba(var(--primary-teal-rgb),0.35)] bg-white text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:text-[var(--theme-accent)]'
                              : 'border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.12)] text-[var(--theme-primary-dark)] hover:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.1)] dark:text-[var(--theme-accent)] dark:hover:bg-[rgba(var(--primary-teal-rgb),0.16)]'
                              } disabled:cursor-not-allowed disabled:opacity-50`}
                          >
                            <span>{option.label}</span>
                            <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                              {preferredMfaMethodLoading === option.method
                                ? 'Updating...'
                                : option.isPrimary
                                  ? 'Preferred'
                                  : option.canSetPreferred
                                    ? 'Make preferred'
                                    : 'Automatic backup'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    <p className="mt-3 text-xs text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                      Recommended: use In-App Approval, Passkeys, or Authenticator App first, then keep email or phone as recovery options.
                    </p>
                  </div>

                  {/* MFA Method Cards */}
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                  {/* Authenticator App (TOTP) */}
                  <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start">
                        <Smartphone className="w-5 h-5 mr-2 text-[var(--theme-primary)] flex-shrink-0" />
                        <div>
                          <h5 className="font-medium pm-text-primary">Authenticator App</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Use Google Authenticator, Authy, or similar apps</p>
                          {isMobileMfaContext && (
                            <span className="mt-1 inline-flex rounded-full bg-[rgba(var(--primary-teal-rgb),0.15)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                              Recommended backup
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${totpEnabled
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                        {totpEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2">
                      {totpEnabled ? (
                        <>
                          <button
                            onClick={disableTotp}
                            disabled={totpLoading}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            {totpLoading ? 'Disabling...' : 'Disable'}
                          </button>
                          <button
                            onClick={regenerateBackupCodes}
                            disabled={totpLoading}
                            className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            <RefreshCw className="w-4 h-4 inline mr-1" />
                            Regenerate
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={startTotpSetup}
                          disabled={totpLoading}
                          className="px-3 py-1.5 bg-[var(--theme-primary)] text-white text-sm rounded-md hover:bg-[var(--theme-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                        >
                          {totpLoading ? 'Setting up...' : 'Setup'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start">
                        <Shield className="w-5 h-5 mr-2 text-[var(--theme-primary)] flex-shrink-0" />
                        <div>
                          <h5 className="font-medium pm-text-primary">In-App Approval</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Approve sign-ins from your TotalAccess mobile app</p>
                          <span className="mt-1 inline-flex rounded-full bg-[rgba(var(--primary-teal-rgb),0.15)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                            Recommended first choice
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${inAppApprovalEnabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                          {inAppApprovalEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        {inAppApprovalStatus?.isLocked && (
                          <span className="inline-flex items-center rounded-full bg-[rgba(var(--primary-teal-rgb),0.15)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                            Locked
                          </span>
                        )}
                      </div>
                    </div>

                    {inAppApprovalStatus?.isLocked && (
                      <p className="mb-2 text-xs text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                        {inAppApprovalStatus.lockReason || 'Required by your role\'s MFA policy'}
                      </p>
                    )}

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => toggleOtpMethod('inapp_approval', !inAppApprovalEnabled)}
                        disabled={inAppApprovalStatus?.isLocked || otpMethodLoading === 'inapp_approval' || (!inAppApprovalEnabled && !!inAppApprovalUnavailableReason)}
                        className={`px-3 py-1.5 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 ${inAppApprovalEnabled
                          ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                          : 'bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] focus:ring-[var(--theme-primary)]'
                          }`}
                      >
                        {otpMethodLoading === 'inapp_approval'
                          ? (inAppApprovalEnabled ? 'Disabling...' : 'Enabling...')
                          : (inAppApprovalEnabled ? 'Disable' : 'Enable')}
                      </button>
                    </div>

                    <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      Ready devices: <span className="font-medium text-gray-900 dark:text-white">{inAppApprovalStatus?.activeDeviceCount || 0}</span>
                      {inAppApprovalUnavailableReason && (
                        <span className="ml-2 text-amber-600 dark:text-amber-400">{inAppApprovalUnavailableReason}</span>
                      )}
                    </div>
                  </div>

                  {/* Passkey MFA */}
                  {passkeysSupportedOnThisDevice && (
                    <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-start">
                          <Key className="w-5 h-5 mr-2 text-[var(--theme-primary)] flex-shrink-0" />
                          <div>
                            <h5 className="font-medium pm-text-primary">Passkey MFA</h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Use passkey as second factor</p>
                            <span className="mt-1 inline-flex rounded-full bg-[rgba(var(--primary-teal-rgb),0.15)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                              Passwordless MFA
                            </span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${webauthnMfaEnabled
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                          {webauthnMfaEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleOtpMethod('webauthn', !webauthnMfaEnabled)}
                          disabled={otpMethodLoading === 'webauthn' || (!webauthnMfaEnabled && webauthnCredentials.length === 0)}
                          className={`px-3 py-1.5 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 ${webauthnMfaEnabled
                            ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                            : 'bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] focus:ring-[var(--theme-primary)]'
                            }`}
                        >
                          {otpMethodLoading === 'webauthn'
                            ? (webauthnMfaEnabled ? 'Disabling...' : 'Enabling...')
                            : (webauthnMfaEnabled ? 'Disable' : 'Enable')}
                        </button>
                      </div>

                      <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                        Registered passkeys: <span className="font-medium text-gray-900 dark:text-white">{webauthnCredentials.length}</span>
                        {webauthnCredentials.length === 0 && (
                          <span className="ml-2 text-amber-600 dark:text-amber-400">Register a passkey first below</span>
                        )}
                      </div>
                    </div>
                  )}
                  </div>

                  {/* Backup verification codes */}
                  <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="mb-3">
                      <h5 className="font-medium pm-text-primary">Backup verification codes</h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Backup codes sent to verified email or phone.</p>
                      <p className="mt-1 text-xs font-medium text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">{automaticBackupReadinessMessage}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start min-w-0">
                            <MessageCircle className="w-5 h-5 mr-2 text-green-600 flex-shrink-0" />
                            <div>
                              <h6 className="font-medium pm-text-primary">Phone backup code</h6>
                              <p className="text-sm text-gray-600 dark:text-gray-400">WhatsApp or SMS code to verified phone</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${whatsappOtpEnabled
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                              {whatsappOtpEnabled ? (whatsappOtpStatus?.isAutomatic ? 'Auto backup' : 'Enabled') : 'Not ready'}
                            </span>
                            {whatsappOtpStatus?.isLocked && (
                              <span className="inline-flex items-center rounded-full bg-[rgba(var(--primary-teal-rgb),0.15)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                                Locked
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <p>Saved phone: <span className="font-medium text-gray-900 dark:text-white">{whatsappOtpStatus?.contact || 'None'}</span></p>
                          {whatsappOtpStatus?.verified ? (
                            <p className="text-green-600 dark:text-green-400">Verified</p>
                          ) : (
                            <p className="text-amber-600 dark:text-amber-400">Not verified</p>
                          )}
                          {whatsappOtpUnavailableReason && (
                            <p className="text-amber-600 dark:text-amber-400">{whatsappOtpUnavailableReason}</p>
                          )}
                          {whatsappOtpStatus?.isLocked && (
                            <p className="text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">{whatsappOtpStatus.lockReason || 'Required by your role\'s MFA policy'}</p>
                          )}
                        </div>
                        <div className="mt-3 flex items-center space-x-2">
                          <button
                            onClick={() => toggleOtpMethod('whatsapp_otp', !whatsappOtpEnabled)}
                            disabled={whatsappOtpStatus?.isLocked || otpMethodLoading === 'whatsapp_otp' || (!whatsappOtpEnabled && !!whatsappOtpUnavailableReason)}
                            className={`px-3 py-1.5 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 ${whatsappOtpEnabled
                              ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                              : 'bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] focus:ring-[var(--theme-primary)]'
                              }`}
                          >
                            {otpMethodLoading === 'whatsapp_otp'
                              ? (whatsappOtpEnabled ? 'Disabling...' : 'Enabling...')
                              : (whatsappOtpEnabled ? 'Disable' : 'Enable')}
                          </button>
                        </div>
                      </div>

                      <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start min-w-0">
                            <Mail className="w-5 h-5 mr-2 text-emerald-600 flex-shrink-0" />
                            <div>
                              <h6 className="font-medium pm-text-primary">Email backup code</h6>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Code to verified email address</p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${emailOtpEnabled
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                              }`}>
                              {emailOtpEnabled ? (emailOtpStatus?.isAutomatic ? 'Auto backup' : 'Enabled') : 'Not ready'}
                            </span>
                            {emailOtpStatus?.isLocked && (
                              <span className="inline-flex items-center rounded-full bg-[rgba(var(--primary-teal-rgb),0.15)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--theme-primary-dark)] dark:bg-[rgba(var(--primary-teal-rgb),0.24)] dark:text-[var(--theme-accent)]">
                                Locked
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          <p>Saved email: <span className="font-medium text-gray-900 dark:text-white">{emailOtpStatus?.contact || 'None'}</span></p>
                          {emailOtpStatus?.verified ? (
                            <p className="text-green-600 dark:text-green-400">Verified</p>
                          ) : (
                            <p className="text-amber-600 dark:text-amber-400">Not verified</p>
                          )}
                          {emailOtpUnavailableReason && (
                            <p className="text-amber-600 dark:text-amber-400">{emailOtpUnavailableReason}</p>
                          )}
                          {emailOtpStatus?.isLocked && (
                            <p className="text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">{emailOtpStatus.lockReason || 'Required by your role\'s MFA policy'}</p>
                          )}
                        </div>
                        <div className="mt-3 flex items-center space-x-2">
                          <button
                            onClick={() => toggleOtpMethod('email_otp', !emailOtpEnabled)}
                            disabled={emailOtpStatus?.isLocked || otpMethodLoading === 'email_otp' || (!emailOtpEnabled && !!emailOtpUnavailableReason)}
                            className={`px-3 py-1.5 text-white text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 ${emailOtpEnabled
                              ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                              : 'bg-[var(--theme-primary)] hover:bg-[var(--theme-primary-dark)] focus:ring-[var(--theme-primary)]'
                              }`}
                          >
                            {otpMethodLoading === 'email_otp'
                              ? (emailOtpEnabled ? 'Disabling...' : 'Enabling...')
                              : (emailOtpEnabled ? 'Disable' : 'Enable')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Passkeys (WebAuthn) - Not supported on Capacitor mobile */}
                  {passkeysSupportedOnThisDevice ? (
                    <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-start min-w-0">
                          <Key className="w-5 h-5 mr-2 text-[var(--theme-primary)] flex-shrink-0" />
                          <div>
                            <h5 className="font-medium pm-text-primary">Passkeys</h5>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Use biometrics or security key</p>
                            {passkeyScopeMessage && (
                              <p className="mt-1 text-xs text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">{passkeyScopeMessage}</p>
                            )}
                            {requiresCentralPasskeyManagement && (
                              <p className="mt-1 text-xs text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                                Register on <strong>{centralPasskeyHostLabel}</strong> for cross-portal use.
                              </p>
                            )}
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 flex-shrink-0">
                          {webauthnCredentials.length} registered
                        </span>
                      </div>

                      {centralPasskeyReturnUrl && !requiresCentralPasskeyManagement && (
                        <div className="mb-3 rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] px-3 py-2 text-sm text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:text-[var(--theme-accent)]">
                          Managing passkeys on shared portal. Reusable on compatible TotalAccess portals.
                          <div className="mt-2">
                            <button
                              type="button"
                              onClick={returnToSourcePortal}
                              className="inline-flex items-center rounded-md bg-[var(--theme-primary)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                            >
                              Return to {centralPasskeyReturnUrl.host}
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Registered Passkeys List */}
                      {webauthnCredentials.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {webauthnCredentials.map((cred) => (
                            <div key={cred.id} className="flex items-center justify-between gap-2 p-3 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                              <div className="flex items-center min-w-0">
                                <Shield className="w-4 h-4 mr-2 text-[var(--theme-primary)] flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{cred.device_name || 'Unnamed Device'}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">Added {new Date(cred.created_at).toLocaleDateString()}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => deletePasskey(cred.id)}
                                disabled={webauthnLoading}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 flex-shrink-0"
                                title="Delete passkey"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {requiresCentralPasskeyManagement ? (
                        <div className="rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] px-3 py-3 text-sm text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] dark:text-[var(--theme-accent)]">
                          <p>
                            New registration should happen on <strong>{centralPasskeyHostLabel}</strong> for shared central passkey.
                          </p>
                          <div className="mt-3">
                            <button
                              type="button"
                              onClick={openCentralPasskeyManagement}
                              className="inline-flex items-center rounded-md bg-[var(--theme-primary)] px-3 py-1.5 font-medium text-white hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                            >
                              <Key className="mr-2 h-4 w-4" />
                              Manage on {centralPasskeyHostLabel}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <input
                            type="text"
                            placeholder="Device name"
                            value={deviceName}
                            onChange={(e) => setDeviceName(e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] text-sm"
                          />
                          <button
                            onClick={registerPasskey}
                            disabled={webauthnLoading || !deviceName.trim()}
                            className="px-3 py-1.5 bg-[var(--theme-primary)] text-white text-sm rounded-md hover:bg-[var(--theme-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] whitespace-nowrap sm:w-auto"
                          >
                            <Plus className="w-4 h-4 inline mr-1" />
                            {webauthnLoading ? 'Adding...' : 'Add'}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                      <div className="rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] bg-[rgba(var(--primary-teal-rgb),0.08)] dark:bg-[rgba(var(--primary-teal-rgb),0.16)] p-3 text-sm text-[var(--theme-primary-dark)] dark:text-[var(--theme-accent)]">
                        Passkeys disabled on mobile/PWA. Use In-App Approval, Authenticator App, or backup code.
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h4 className="text-md font-semibold pm-text-primary">My Devices</h4>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Review signed-in devices and trusted browsers.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => window.dispatchEvent(new CustomEvent('open-my-devices-modal'))}
                      className="inline-flex items-center justify-center rounded-md bg-[var(--theme-primary)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                    >
                      Open My Devices
                    </button>
                  </div>
                </div>

                {/* Biometric App Lock - Only show on Capacitor mobile when biometrics are available */}
                {Capacitor.isNativePlatform() && biometricAvailable && (
                  <div className="bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4 mb-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 mr-3 text-green-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {biometricType === 'face' ? (
                            <>
                              <circle cx="12" cy="12" r="10" />
                              <circle cx="9" cy="10" r="1" />
                              <circle cx="15" cy="10" r="1" />
                              <path d="M9 15c.83.67 2 1 3 1s2.17-.33 3-1" />
                            </>
                          ) : (
                            <>
                              <path d="M12 10V14M8 6C8 6 6 7 6 10V14C6 17 8 18 8 18M16 6C16 6 18 7 18 10V14C18 17 16 18 16 18" />
                              <path d="M12 2C12 2 8 4 8 10M12 2C12 2 16 4 16 10M12 2V6" />
                              <circle cx="12" cy="12" r="3" />
                              <path d="M12 15V22" />
                            </>
                          )}
                        </svg>
                        <div>
                          <h5 className="font-medium pm-text-primary">
                            {biometricType === 'face' ? 'Face Unlock' : biometricType === 'iris' ? 'Iris Unlock' : 'Fingerprint Unlock'}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {biometricEnabled
                              ? 'Requires biometric verification when returning from background'
                              : `Use ${biometricType === 'face' ? 'face' : biometricType === 'iris' ? 'iris' : 'fingerprint'} to unlock app`
                            }
                          </p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={biometricEnabled}
                          onChange={handleBiometricToggle}
                          disabled={biometricLoading}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-500 peer-checked:bg-green-600 ${biometricLoading ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Lock Screen PIN & Change Password */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">

                {/* Lock Screen PIN */}
                <div className="bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                  <h4 className="text-md font-semibold pm-text-primary mb-2">Lock Screen PIN</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    6-digit PIN for quick unlock after inactivity. Cannot be used to sign in.
                  </p>

                  {pinError && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                      {pinError}
                    </div>
                  )}
                  {pinSuccess && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
                      {pinSuccess}
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start">
                      <Hash className="w-5 h-5 mr-2 text-[var(--theme-primary)] flex-shrink-0" />
                      <div>
                        <h5 className="font-medium pm-text-primary">6-Digit PIN</h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {hasPin ? 'PIN set — use to unlock lock screen' : 'No PIN set — use password to unlock'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${hasPin
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                      {hasPin ? 'Enabled' : 'Not Set'}
                    </span>
                  </div>

                  {!showPinSetup ? (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setShowPinSetup(true)
                          setPinError('')
                          setPinSuccess('')
                          setPinData({ pin: '', confirmPin: '', password: '' })
                        }}
                        disabled={pinLoading}
                        className="px-3 py-1.5 bg-[var(--theme-primary)] text-white text-sm rounded-md hover:bg-[var(--theme-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                      >
                        {hasPin ? 'Change PIN' : 'Set PIN'}
                      </button>
                      {hasPin && (
                        <button
                          onClick={handleRemovePin}
                          disabled={pinLoading}
                          className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          Remove PIN
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="pm-label">New 6-Digit PIN</label>
                        <input
                          type="password"
                          inputMode="numeric"
                          value={pinData.pin}
                          onChange={(e) => setPinData(prev => ({ ...prev, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                          placeholder="Enter 6-digit PIN"
                          className="pm-input"
                          maxLength={6}
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Must be exactly 6 numeric digits</p>
                      </div>
                      <div>
                        <label className="pm-label">Confirm PIN</label>
                        <input
                          type="password"
                          inputMode="numeric"
                          value={pinData.confirmPin}
                          onChange={(e) => setPinData(prev => ({ ...prev, confirmPin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                          placeholder="Re-enter 6-digit PIN"
                          className="pm-input"
                          maxLength={6}
                        />
                      </div>
                      <div>
                        <label className="pm-label">Current Password</label>
                        <input
                          type="password"
                          value={pinData.password}
                          onChange={(e) => setPinData(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Enter your password to confirm"
                          className="pm-input"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Required to verify your identity</p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSetPin}
                          disabled={pinLoading || pinData.pin.length !== 6 || pinData.confirmPin.length !== 6 || !pinData.password}
                          className="flex-1 px-3 py-1.5 bg-[var(--theme-primary)] text-white text-sm rounded-md hover:bg-[var(--theme-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                        >
                          {pinLoading ? 'Saving...' : hasPin ? 'Update PIN' : 'Set PIN'}
                        </button>
                        <button
                          onClick={() => {
                            setShowPinSetup(false)
                            setPinData({ pin: '', confirmPin: '', password: '' })
                            setPinError('')
                          }}
                          className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* TOTP Setup Modal */}
                {showTotpSetup && totpSetup && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 max-w-md w-full max-h-[90vh] overflow-y-auto sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold pm-text-primary">Setup Authenticator App</h3>
                        <button
                          onClick={() => {
                            setShowTotpSetup(false)
                            setTotpVerificationCode('')
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Scan this QR code with your authenticator app:
                        </p>

                        <div className="flex justify-center p-4 bg-white rounded-lg">
                          <QRCodeCanvas value={totpSetup.otpauthUrl} size={200} />
                        </div>

                        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Or enter manually:</p>
                          <code className="text-sm text-gray-900 dark:text-white break-all">{totpSetup.manualEntryKey}</code>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Enter verification code
                          </label>
                          <input
                            type="text"
                            value={totpVerificationCode}
                            onChange={(e) => setTotpVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                            placeholder="000000"
                            maxLength={6}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          />
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setShowTotpSetup(false)
                              setTotpVerificationCode('')
                            }}
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={enableTotp}
                            disabled={totpVerificationCode.length !== 6 || totpLoading}
                            className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          >
                            {totpLoading ? 'Verifying...' : 'Enable'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TOTP Disable Modal */}
                {showTotpDisable && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 max-w-md w-full max-h-[90vh] overflow-y-auto sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold pm-text-primary">Disable Authenticator App</h3>
                        <button
                          onClick={() => {
                            setShowTotpDisable(false)
                            setTotpDisablePassword('')
                            setTotpDisableCode('')
                            setTotpError('')
                          }}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        {totpError && (
                          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                            <p className="text-sm text-red-600 dark:text-red-400">{totpError}</p>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Enter your password
                          </label>
                          <input
                            type="password"
                            value={totpDisablePassword}
                            onChange={(e) => setTotpDisablePassword(e.target.value)}
                            placeholder="Password"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Enter TOTP code
                          </label>
                          <input
                            type="text"
                            value={totpDisableCode}
                            onChange={(e) => setTotpDisableCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                            placeholder="000000"
                            maxLength={6}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          />
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setShowTotpDisable(false)
                              setTotpDisablePassword('')
                              setTotpDisableCode('')
                              setTotpError('')
                            }}
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleTotpDisable}
                            disabled={totpLoading}
                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            {totpLoading ? 'Disabling...' : 'Disable'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Backup Codes Modal */}
                {showBackupCodes && backupCodes.length > 0 && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 max-w-md w-full max-h-[90vh] overflow-y-auto sm:p-6">
                      <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between">
                        <Image
                          src="/logo/2-logo.png"
                          alt="TotalAccess"
                          width={120}
                          height={40}
                          className="object-contain"
                        />
                        <div className="flex items-center justify-between space-x-3">
                          <h3 className="text-lg font-semibold pm-text-primary">Backup Codes</h3>
                          <button
                            onClick={() => setShowBackupCodes(false)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            <strong>Important:</strong> Save these backup codes in a safe place. Each code can only be used once.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                          {backupCodes.map((code, index) => (
                            <code key={index} className="text-sm text-gray-900 dark:text-white font-mono">
                              {code}
                            </code>
                          ))}
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={downloadBackupCodes}
                            className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            <Download className="w-4 h-4 inline mr-1" />
                            Download
                          </button>
                          <button
                            onClick={() => setShowBackupCodes(false)}
                            className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Password Change */}
                <div className="bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
                  <h4 className="text-md font-semibold pm-text-primary mb-2">Change Password</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Update your account password. Use a strong, unique password you don't reuse elsewhere.
                  </p>
                  <form onSubmit={handlePasswordChange} className="space-y-3">
                    <div>
                      <label htmlFor="currentPassword" className="pm-label">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          id="currentPassword"
                          name="currentPassword"
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          className="pm-input pr-10"
                          placeholder="Current password"
                          required
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                          onClick={() => setShowCurrentPassword(v => !v)}
                        >
                          {showCurrentPassword ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {passwordErrors.currentPassword && (
                        <p className="text-red-600 text-sm mt-1">{passwordErrors.currentPassword}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="newPassword" className="pm-label">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          id="newPassword"
                          name="newPassword"
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          className="pm-input pr-10"
                          placeholder="New password"
                          required
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                          onClick={() => setShowNewPassword(v => !v)}
                        >
                          {showNewPassword ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      {/* Password validation message */}
                      {passwordValidation.message && (
                        <div className="mt-1 text-sm text-red-600">
                          {passwordValidation.message}
                        </div>
                      )}
                      {!passwordValidation.message && passwordData.newPassword && passwordValidation.isValid && (
                        <div className="mt-1 text-sm text-green-600">
                          ✓ Password meets requirements
                        </div>
                      )}
                      {passwordErrors.newPassword && (
                        <p className="text-red-600 text-sm mt-1">{passwordErrors.newPassword}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="pm-label">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          id="confirmPassword"
                          name="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          className="pm-input pr-10"
                          placeholder="Confirm new password"
                          required
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                          onClick={() => setShowConfirmPassword(v => !v)}
                        >
                          {showConfirmPassword ? (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>

                      {/* Password match validation (UI only; submit validation remains unchanged) */}
                      {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <div className="mt-1 text-sm text-red-600 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Passwords do not match
                        </div>
                      )}
                      {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                        <div className="mt-1 text-sm text-green-600 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Passwords match
                        </div>
                      )}

                      {passwordErrors.confirmPassword && (
                        <p className="text-red-600 text-sm mt-1">{passwordErrors.confirmPassword}</p>
                      )}
                    </div>

                    {passwordSuccess && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-700 dark:text-green-300">
                        {passwordSuccess}
                      </div>
                    )}

                    {passwordErrors.submit && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                        {passwordErrors.submit}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={changingPassword}
                      className="px-3 py-1.5 bg-[var(--theme-primary)] text-white text-sm rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50"
                    >
                      {changingPassword ? 'Changing...' : 'Change Password'}
                    </button>
                  </form>
                </div>
                </div>

              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div>
                <h3 className="text-lg font-semibold pm-text-primary mb-4">Notifications & Sounds</h3>

                {/* Push Notifications Section - Native mobile app or installed PWA */}
                {(Capacitor.isNativePlatform() || (typeof window !== 'undefined' && isStandalonePwa())) && (
                  <div className="mb-6 p-4 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2 flex-wrap">
                          {Capacitor.isNativePlatform() ? '📱 Push Notifications' : '🌐 PWA Push Notifications'}
                          {pushNotificationStatus === 'granted' && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Enabled</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {pushNotificationStatus === 'loading' && 'Checking notification status...'}
                          {pushNotificationStatus === 'granted' && (Capacitor.isNativePlatform() ? 'You will receive push notifications on this device' : 'This installed PWA is allowed to receive push notifications on this device')}
                          {pushNotificationStatus === 'denied' && 'Push notifications are blocked. Enable them in your device or browser settings.'}
                          {pushNotificationStatus === 'prompt' && 'Use the button to grant notification permission, fetch a token, and register this device for alerts'}
                          {pushNotificationStatus === 'unavailable' && 'Push notifications are not available in this runtime'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {pushNotificationStatus !== 'loading' && pushNotificationStatus !== 'unavailable' && (
                          <button
                            type="button"
                            onClick={handleEnablePushNotifications}
                            disabled={enablingPush}
                            className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 text-sm whitespace-nowrap"
                          >
                            {enablingPush ? 'Enabling Push...' : pushNotificationStatus === 'granted' ? 'Reconnect Push' : 'Enable Push Notifications'}
                          </button>
                        )}
                        {pushNotificationStatus === 'granted' && (
                          <span className="text-green-600 dark:text-green-400 text-2xl">✓</span>
                        )}
                      </div>
                    </div>
                    {pushSuccess && (
                      <p className="text-green-600 dark:text-green-400 text-sm mt-2">{pushSuccess}</p>
                    )}
                    {pushError && (
                      <p className="text-red-600 dark:text-red-400 text-sm mt-2">{pushError}</p>
                    )}
                  </div>
                )}

                {!Capacitor.isNativePlatform() && typeof window !== 'undefined' && isStandalonePwa() && (
                  <div className="mb-6 p-4 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100">Push Diagnostics</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Runs a live browser-side check for permission, Firebase worker readiness, token creation, and server registration.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRunPushDiagnostics}
                        disabled={runningPushDiagnostics}
                        className="px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 disabled:opacity-50 text-sm whitespace-nowrap flex-shrink-0"
                      >
                        {runningPushDiagnostics ? 'Running Diagnostics...' : 'Run Push Diagnostics'}
                      </button>
                    </div>

                    {pushDiagnostics && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700">
                          <div className="font-medium text-gray-900 dark:text-gray-100 mb-2">Runtime</div>
                          <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                            <li><span className="font-medium">Supported:</span> {pushDiagnostics.supported ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Permission:</span> {pushDiagnostics.permission}</li>
                            <li><span className="font-medium">Configured VAPID:</span> {pushDiagnostics.hasConfiguredVapidKey ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Using Firebase default VAPID:</span> {pushDiagnostics.usingFirebaseDefaultVapid ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">VAPID configuration valid:</span> {pushDiagnostics.hasValidVapidKey ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Initialized:</span> {pushDiagnostics.isInitialized ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">App ID:</span> {pushDiagnostics.portalIdentity?.appId || '—'}</li>
                          </ul>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700">
                          <div className="font-medium text-gray-900 dark:text-gray-100 mb-2">Service Worker</div>
                          <ul className="space-y-1 text-gray-700 dark:text-gray-300 break-all">
                            <li><span className="font-medium">Scope:</span> {pushDiagnostics.serviceWorker.scope || '—'}</li>
                            <li><span className="font-medium">Active state:</span> {pushDiagnostics.serviceWorker.activeState || '—'}</li>
                            <li><span className="font-medium">Push subscription exists:</span> {pushDiagnostics.pushSubscription.existingSubscription ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Push subscribe attempted:</span> {pushDiagnostics.pushSubscription.subscribeAttempted ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Push subscribe succeeded:</span> {pushDiagnostics.pushSubscription.subscribeSucceeded ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Token attempted:</span> {pushDiagnostics.tokenAttempted ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Token fetched:</span> {pushDiagnostics.tokenFetchSucceeded ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Push endpoint:</span> {pushDiagnostics.pushSubscription.endpointPreview || '—'}</li>
                            <li><span className="font-medium">Token preview:</span> {pushDiagnostics.tokenPreview || '—'}</li>
                            {pushDiagnostics.pushSubscription.error && (
                              <li className="text-red-600 dark:text-red-400"><span className="font-medium">Push subscribe error:</span> {pushDiagnostics.pushSubscription.error}</li>
                            )}
                          </ul>
                        </div>
                        <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 md:col-span-2">
                          <div className="font-medium text-gray-900 dark:text-gray-100 mb-2">Server Registration</div>
                          <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                            <li><span className="font-medium">Cached token:</span> {pushDiagnostics.hasCachedToken ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Registration attempted:</span> {pushDiagnostics.registrationAttempted ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Registered with server:</span> {pushDiagnostics.registrationSucceeded ? 'Yes' : 'No'}</li>
                            <li><span className="font-medium">Current registration state:</span> {pushDiagnostics.tokenRegisteredWithServer ? 'Linked' : 'Not linked'}</li>
                            {pushDiagnostics.error && (
                              <li className="text-red-600 dark:text-red-400"><span className="font-medium">Error:</span> {pushDiagnostics.error}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handlePreferencesSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">Email Notifications</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">Receive notifications via email</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.emailNotifications}
                          onChange={(e) => setPreferences({ ...preferences, emailNotifications: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--theme-accent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">SMS Notifications</div>
                        <div className="text-sm text-gray-600">Receive notifications via SMS</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.smsNotifications}
                          onChange={(e) => setPreferences({ ...preferences, smsNotifications: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--theme-accent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Security Alerts</div>
                        <div className="text-sm text-gray-600">Important security notifications</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.securityAlerts}
                          onChange={(e) => setPreferences({ ...preferences, securityAlerts: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--theme-accent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Marketing Emails</div>
                        <div className="text-sm text-gray-600">Receive promotional emails</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.marketingEmails}
                          onChange={(e) => setPreferences({ ...preferences, marketingEmails: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--theme-accent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
                      </label>
                    </div>
                  </div>

                  {/* Message sounds (thread-type specific) */}
                  <div className="mt-6 p-4 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="font-medium text-gray-900 dark:text-gray-100">Message Notification Sounds</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Pick different sounds for schedule threads, job threads, client threads, and 1:1 chats.
                    </div>

                    <div className="mt-4 space-y-3">
                      {(
                        [
                          { key: 'scheduleThreads', label: 'Schedule Threads', help: 'Daily schedules and shift notifications' },
                          { key: 'jobThreads', label: 'Job Threads', help: 'Job/task updates and work notifications' },
                          { key: 'clientThreads', label: 'Client Threads', help: 'Group/client discussions' },
                          { key: 'directThreads', label: '1:1 Threads', help: 'Direct messages with one other person' }
                        ] as Array<{ key: MessageThreadSoundCategory; label: string; help: string }>
                      ).map(row => (
                        <div key={row.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.label}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">{row.help}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={messageSoundPrefs[row.key]}
                              onChange={(e) => updateMessageSoundPref(row.key, e.target.value as MessageSoundOptionId)}
                              className="w-full sm:w-56 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                            >
                              {MESSAGE_SOUND_OPTIONS.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => previewMessageSound(messageSoundPrefs[row.key])}
                              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                            >
                              Preview
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={resetMessageSoundPrefs}
                        className="text-sm px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        Reset to defaults
                      </button>
                    </div>
                  </div>

                  {/* Voice Call Sounds */}
                  <div className="mt-6 p-4 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      📞 Voice Call Sounds
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Customize your incoming call ringtone and outgoing dial tone for voice calls.
                    </div>

                    <div className="mt-4 space-y-4">
                      {/* Incoming Ringtone */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Incoming Call Ringtone</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Sound when someone calls you</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={callSoundPrefs.incomingRingtone}
                            onChange={(e) => updateCallSoundPref('incomingRingtone', e.target.value)}
                            className="w-full sm:w-56 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          >
                            {CALL_RINGTONE_OPTIONS.map(opt => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => previewCallSound('ringtone')}
                            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                          >
                            Preview
                          </button>
                        </div>
                      </div>

                      {/* Ringtone Volume */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Ringtone Volume</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Adjust incoming call volume</div>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-56">
                          <span className="text-xs text-gray-500">🔈</span>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={callSoundPrefs.ringtoneVolume}
                            onChange={(e) => updateCallSoundPref('ringtoneVolume', parseFloat(e.target.value))}
                            className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
                          />
                          <span className="text-xs text-gray-500">🔊</span>
                        </div>
                      </div>

                      {/* Outgoing Dial Tone */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Outgoing Dial Tone</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Sound when you&apos;re calling someone</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={callSoundPrefs.outgoingDialtone}
                            onChange={(e) => updateCallSoundPref('outgoingDialtone', e.target.value)}
                            className="w-full sm:w-56 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          >
                            {CALL_DIALTONE_OPTIONS.map(opt => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => previewCallSound('dialtone')}
                            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                          >
                            Preview
                          </button>
                        </div>
                      </div>

                      {/* Dial Tone Volume */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Dial Tone Volume</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">Adjust outgoing call dial tone</div>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-56">
                          <span className="text-xs text-gray-500">🔈</span>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={callSoundPrefs.dialToneVolume}
                            onChange={(e) => updateCallSoundPref('dialToneVolume', parseFloat(e.target.value))}
                            className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
                          />
                          <span className="text-xs text-gray-500">🔊</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-end">
                      <button
                        type="button"
                        onClick={resetCallSoundPrefs}
                        className="text-sm px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        Reset to defaults
                      </button>
                    </div>
                  </div>

                  {/* Push Notification Sounds (Mobile + installed PWA) */}
                  {supportsPushSoundPreferences && (
                    <div className="mt-6 p-4 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                      <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        🔔 Push Notification Sounds
                        {pushSoundPrefsSaving && (
                          <span className="text-xs text-[var(--theme-primary)] dark:text-[var(--theme-accent)] animate-pulse">Saving...</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Customize sounds for different types of push notifications. Settings sync across all your devices, and the installed PWA foreground alerts will use them here on this device.
                      </div>
                      {!Capacitor.isNativePlatform() && (
                        <div className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                          Browser-managed background notifications may still use the browser or OS default sound rules, but live in-app PWA alerts will follow these selections.
                        </div>
                      )}

                      {pushSoundPrefsLoading ? (
                        <div className="mt-4 flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--theme-primary)]"></div>
                          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Loading preferences...</span>
                        </div>
                      ) : (
                        <>
                          {/* Sound Pack Selector */}
                          <div className="mt-4 mb-4 p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-600">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Sound Style</div>
                            <div className="flex flex-wrap gap-2">
                              {SOUND_PACK_OPTIONS.map(pack => (
                                <button
                                  key={pack.id}
                                  type="button"
                                  onClick={() => updatePushSoundPref('soundPack', pack.id)}
                                  className={`px-4 py-2 text-sm rounded-md border transition-colors ${pushSoundPrefs.soundPack === pack.id
                                    ? 'bg-[var(--theme-primary)] text-white border-[var(--theme-primary)]'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                >
                                  {pack.label}
                                </button>
                              ))}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {SOUND_PACK_OPTIONS.find(p => p.id === pushSoundPrefs.soundPack)?.description}
                            </div>
                          </div>

                          {/* Per-category sound settings */}
                          <div className="mt-4 space-y-3">
                            {PUSH_NOTIFICATION_CATEGORIES.map(cat => (
                              <div key={cat.key} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                                    <span>{cat.icon}</span>
                                    {cat.label}
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">{cat.description}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={pushSoundPrefs[cat.key] as string}
                                    onChange={(e) => updatePushSoundPref(cat.key, e.target.value as PushSoundOptionId)}
                                    className="w-full sm:w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] text-sm"
                                  >
                                    {PUSH_SOUND_OPTIONS.map(opt => (
                                      <option key={opt.id} value={opt.id}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => previewPushSound(cat.key)}
                                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                  >
                                    ▶
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Volume and Vibration */}
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Notification Volume</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">Adjust volume for all push notification sounds</div>
                              </div>
                              <div className="flex items-center gap-3 w-full sm:w-48">
                                <span className="text-xs text-gray-500">🔈</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.1"
                                  value={pushSoundPrefs.volume}
                                  onChange={(e) => updatePushSoundPref('volume', parseFloat(e.target.value))}
                                  className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-600"
                                />
                                <span className="text-xs text-gray-500">🔊</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Vibrate with Sound</div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">Device vibrates when notification sounds play</div>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={pushSoundPrefs.vibrate}
                                  onChange={(e) => updatePushSoundPref('vibrate', e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--theme-accent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
                              </label>
                            </div>
                          </div>

                          <div className="mt-4 flex items-center justify-end">
                            <button
                              type="button"
                              onClick={resetPushSoundPrefs}
                              disabled={pushSoundPrefsSaving}
                              className="text-sm px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                            >
                              Reset to defaults
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Quiet Hours / Communication Hours */}
                  <div className="mt-6 p-4 bg-white dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          🌙 Quiet Hours
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Suppress non-urgent push notifications (attendance reminders, summaries, predictive warnings) during your quiet hours. Security alerts and incoming calls will still come through.
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.quietHoursEnabled}
                          onChange={(e) => setPreferences({ ...preferences, quietHoursEnabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--theme-accent)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--theme-primary)]"></div>
                      </label>
                    </div>

                    {preferences.quietHoursEnabled && (
                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Start</label>
                          <input
                            type="time"
                            value={preferences.quietHoursStart}
                            onChange={(e) => setPreferences({ ...preferences, quietHoursStart: e.target.value })}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">End</label>
                          <input
                            type="time"
                            value={preferences.quietHoursEnd}
                            onChange={(e) => setPreferences({ ...preferences, quietHoursEnd: e.target.value })}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {preferencesSuccess && (
                    <div className="text-green-600 text-sm">{preferencesSuccess}</div>
                  )}

                  <button
                    type="submit"
                    disabled={savingPreferences}
                    className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-md hover:bg-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] disabled:opacity-50"
                  >
                    {savingPreferences ? 'Saving...' : 'Save Notification Settings'}
                  </button>
                </form>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && <ContactsTab />}

            {/* Terms & Privacy Tab */}
            {activeTab === 'terms' && <TermsTab />}
          </div>
        </div>

        <ModalFooter className="">
          <button
            type="button"
            onClick={handleClose}
            className="w-full sm:w-auto justify-center px-6 py-2.5 rounded-lg font-medium text-white bg-gradient-to-r/50 from-[var(--theme-primary)] to-[var(--theme-primary-dark)] hover:from-[var(--theme-primary-dark)] hover:to-[var(--theme-primary-dark)] transition-all flex items-center gap-2"
          >
            Close
          </button>
        </ModalFooter>
      </div>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-[var(--theme-primary)] to-[var(--theme-primary-dark)] px-5 py-5 text-white sm:px-8 sm:py-6">
              <div className="text-center">
                <div className="mx-auto h-12 w-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold mb-2">
                  {otpModalMode === 'mfa_enable'
                    ? `Enable ${pendingMfaOtpMethod === 'email_otp' ? 'Email' : 'WhatsApp'} OTP`
                    : `Verify ${otpType === 'email' ? 'Email' : 'Phone Number'}`}
                </h2>
                <p className="text-white/80 text-sm break-words">
                  {otpModalMode === 'mfa_enable'
                    ? `We've sent a verification code to ${newContactValue}. Enter it to enable this MFA method.`
                    : `We've sent a verification code to ${newContactValue}`}
                </p>
              </div>
            </div>

            {/* Form Content */}
            <div className="px-5 py-5 sm:px-8 sm:py-6">
              {/* Error Message */}
              {fieldErrors.otp && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-red-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-red-700 dark:text-red-300">{fieldErrors.otp}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleVerifyOtp} className="space-y-6">
                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 text-center">
                    {otpModalMode === 'mfa_enable'
                      ? 'Enter the 6-digit code to enable MFA'
                      : 'Enter the 6-digit verification code'}
                  </label>
                  <div className="flex justify-center space-x-2 mb-6 sm:space-x-3">
                    {verificationCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-10 h-12 text-center text-lg font-semibold border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-[var(--theme-primary)] dark:focus:border-[var(--theme-accent)] focus:outline-none transition-colors sm:w-12"
                        autoComplete="off"
                      />
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col space-y-3">
                  <button
                    type="submit"
                    disabled={verifyingOtp || verificationCode.join('').length !== 6}
                    className="w-full bg-gradient-to-r from-[var(--theme-primary)] to-[var(--theme-primary-dark)] text-white py-3 px-6 rounded-lg font-medium hover:from-[var(--theme-primary-dark)] hover:to-[var(--theme-primary-dark)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--theme-primary)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                  >
                    {verifyingOtp ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Verifying...
                      </div>
                    ) : (
                      otpModalMode === 'mfa_enable' ? 'Enable MFA Method' : 'Verify Code'
                    )}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendCooldown > 0 || sendingOtp}
                      className="text-[var(--theme-primary)] dark:text-[var(--theme-accent)] hover:text-[var(--theme-primary-dark)] dark:hover:text-[var(--theme-accent)] text-sm font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sendingOtp ? 'Sending...' : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={closeOtpModal}
                    className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-300 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showInAppApprovalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-md w-full max-h-[90vh] overflow-y-auto dark:bg-gray-900">
            <div className="bg-gradient-to-r from-[var(--theme-primary)] to-[var(--theme-primary-dark)] px-5 py-5 text-white sm:px-8 sm:py-6">
              <div className="text-center">
                <div className="mx-auto h-12 w-12 bg-white/20 rounded-full flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-bold mb-2">Approve In-App MFA</h2>
                <p className="text-white/80 text-sm">
                  We sent a live approval request to your signed-in TotalAccess mobile app. Approve it there to finish enabling this MFA method.
                </p>
              </div>
            </div>

            <div className="px-5 py-5 space-y-4 sm:px-8 sm:py-6">
              <div className="rounded-lg border border-[rgba(var(--primary-teal-rgb),0.35)] bg-[rgba(var(--primary-teal-rgb),0.08)] p-4 text-sm text-[var(--theme-primary-dark)] dark:border-[rgba(var(--primary-teal-rgb),0.4)] dark:bg-[rgba(var(--primary-teal-rgb),0.2)] dark:text-[var(--theme-accent)]">
                <p className="font-medium">Current status: Waiting for approval</p>
                {pendingInAppApprovalExpiresAt && (
                  <p className="mt-2 text-xs opacity-80">
                    Expires at {new Date(pendingInAppApprovalExpiresAt).toLocaleTimeString()}.
                  </p>
                )}
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-300">
                This checks that your device can really receive and approve prompts before we enable the method. Tiny trust exercise, big security payoff.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={closeInAppApprovalModal}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-300 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onboard With Client Modal */}
      <OnboardWithClientModal
        isOpen={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        onSuccess={() => {
          // Refresh client data and join requests after successful onboarding
          fetchClientData()
          fetchJoinRequests()
        }}
      />
    </div>
    </div>
  )
}
